import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "react-modal";
import moment from "moment";
import "moment/locale/th";

// Material-UI & Components
import Grid from "components/Grid/GridContainer.js";
import Col from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";

// API Service
import { KpiService } from "_services/kpi.service";

const styles = {
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "400",
    fontFamily: '"Sarabun", sans-serif',
    marginBottom: "3px"
  },
  modalOverlay: {
    backgroundColor: "rgba(0,0,0,0.65)",
    zIndex: 1050
  },
  modalContent: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "1100px",
    maxWidth: "96vw",
    maxHeight: "94vh",
    overflowY: "auto",
    padding: "25px",
    borderRadius: "8px",
    fontFamily: '"Sarabun", sans-serif'
  },
  criteriaCard: {
    borderRadius: "6px",
    padding: "10px 14px",
    marginBottom: "8px",
    borderLeft: "5px solid"
  }
};
const useStyles = makeStyles(styles);

const MyKpi = () => {
  const classes = useStyles();
  moment.locale("th");

  const currentUser = JSON.parse(localStorage.getItem("currentUser")) || {};
  const currentUserName =
    currentUser.username || currentUser.userName || currentUser.name || "";

  const isAdmin = useMemo(() => {
    return (
      currentUser.role === "Admin" ||
      currentUser.userName === "madarakung" ||
      (Array.isArray(currentUser.userDepartment) &&
        currentUser.userDepartment.some(
          d =>
            d.departmentKey === 105 ||
            d.departmentKey === 108 ||
            (d.departmentKey === 143 && d.userLevelKey === 3) ||
            d.userLevelKey === 4
        ))
    );
  }, [currentUser]);

  // --- States ---
  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState("");
  const [summaryData, setSummaryData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);

  // Modals
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isIndividualModalOpen, setIsIndividualModalOpen] = useState(false);
  const [isDefendModalOpen, setIsDefendModalOpen] = useState(false);
  const [defendActiveTab, setDefendActiveTab] = useState("form"); // "form" | "compare"
  const [isSelfAssessModalOpen, setIsSelfAssessModalOpen] = useState(false);
  const [isClarifyModalOpen, setIsClarifyModalOpen] = useState(false);
  const [isWeightModalOpen, setIsWeightModalOpen] = useState(false);

  const [activeItem, setActiveItem] = useState(null);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [clarifyingAssignment, setClarifyingAssignment] = useState(null);
  const [clarificationText, setClarificationText] = useState("");
  const [isSubmittingClarification, setIsSubmittingClarification] = useState(
    false
  );

  const [weightAssignment, setWeightAssignment] = useState(null);
  const [weightInputValue, setWeightInputValue] = useState("");
  const [isSavingWeight, setIsSavingWeight] = useState(false);

  // Helper to parse DefendRemark (JSON vs plain text)
  const parseDefendData = remark => {
    if (!remark) return null;
    try {
      if (typeof remark === "string" && remark.trim().startsWith("{")) {
        return JSON.parse(remark);
      }
      if (typeof remark === "object") return remark;
    } catch (e) {}
    return { reason: remark };
  };

  // Individual Form State
  const initialIndividualState = {
    PeriodID: "",
    KpiType: 2, // INDIVIDUAL
    Code: "",
    KpiName: "",
    PlanProjectName: "",
    StrategicAlignment: "",
    TargetScoreLevel: 3,
    WeightType: 2, // Flexible
    DefaultWeight: 10.0,
    UnitName: "ร้อยละ",
    Description: "",
    CriteriaLevel1: "",
    CriteriaLevel2: "",
    CriteriaLevel3: "",
    CriteriaLevel4: "",
    CriteriaLevel5: "",
    Criteria12M_Level1: "",
    Criteria12M_Level2: "",
    Criteria12M_Level3: "",
    Criteria12M_Level4: "",
    Criteria12M_Level5: "",
    CalculationFormula: "",
    ScoreExample: "",
    BaselineData: "",
    AssignedUserNames: []
  };
  const [individualFormData, setIndividualFormData] = useState(
    initialIndividualState
  );

  // Defend Form State
  const initialDefendState = {
    KpiName: "",
    TargetScoreLevel: 3,
    DefaultWeight: 10.0,
    Description: "",
    CriteriaLevel1: "",
    CriteriaLevel2: "",
    CriteriaLevel3: "",
    CriteriaLevel4: "",
    CriteriaLevel5: "",
    Criteria12M_Level1: "",
    Criteria12M_Level2: "",
    Criteria12M_Level3: "",
    Criteria12M_Level4: "",
    Criteria12M_Level5: "",
    CalculationFormula: "",
    ScoreExample: "",
    BaselineData: "",
    Reason: "",
    IsSingleUserFork: true,
    TargetUserName: currentUserName
  };
  const [defendFormData, setDefendFormData] = useState(initialDefendState);

  // Self-Assessment Form State
  const [selfAssessFormData, setSelfAssessFormData] = useState({
    ActualAchievement: "",
    SelfScoreLevel: 3,
    AttachmentFileUrls: ""
  });

  // KPI Sorting and Reordering State
  const [sortField, setSortField] = useState("custom");
  const [sortOrder, setSortOrder] = useState("asc");
  const [localAssignments, setLocalAssignments] = useState([]);
  const [isReordering, setIsReordering] = useState(false);

  useEffect(() => {
    if (summaryData?.assignments) {
      setLocalAssignments(summaryData.assignments);
    } else {
      setLocalAssignments([]);
    }
  }, [summaryData?.assignments]);

  useEffect(() => {
    Modal.setAppElement("body");
    fetchPeriods();
  }, []);

  useEffect(() => {
    if (selectedPeriodId) {
      fetchMySummary(selectedPeriodId);
    } else {
      setSummaryData(null);
    }
  }, [selectedPeriodId]);

  const fetchPeriods = async () => {
    try {
      const response = await KpiService.getAllPeriods();
      if (response && response.data && response.data.data) {
        const list = response.data.data;
        setPeriods(list);
        if (list.length > 0) {
          const currentTarget = KpiService.findCurrentPeriod(list);
          setSelectedPeriodId(
            currentTarget ? currentTarget.periodID : list[0].periodID
          );
        }
      }
    } catch (error) {
      console.error("Error fetching periods:", error);
    }
  };

  const fetchMySummary = async periodId => {
    setIsLoading(true);
    try {
      const response = await KpiService.getMySummary(periodId, currentUserName);
      if (response && response.data && response.data.data) {
        setSummaryData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching my KPI summary:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper formatting
  const formatThaiDate = dateStr => {
    if (!dateStr) return "-";
    const d = moment(dateStr);
    if (!d.isValid()) return "-";
    return d.add(543, "years").format("DD MMM YYYY, HH:mm น.");
  };

  // --- Handlers: Modal Openers ---
  const handleOpenDetailModal = assignment => {
    setActiveItem(assignment.kpiItem);
    setActiveAssignment(assignment);
    setIsDetailModalOpen(true);
  };

  const handleOpenCreateIndividualModal = () => {
    setEditingItemId(null);
    setIndividualFormData({
      ...initialIndividualState,
      PeriodID: selectedPeriodId,
      Code: `MY-KPI-${(displayedAssignments?.length || 0) + 1}`,
      AssignedUserNames: [currentUserName]
    });
    setIsIndividualModalOpen(true);
  };

  const handleOpenEditIndividualModal = assignment => {
    const item = assignment.kpiItem;
    setEditingItemId(item.kpiItemID);
    setIndividualFormData({
      PeriodID: item.periodID,
      KpiType: 2,
      Code: item.code || "",
      KpiName: item.kpiName || "",
      PlanProjectName: item.planProjectName || "",
      StrategicAlignment: item.strategicAlignment || "",
      TargetScoreLevel: item.targetScoreLevel || 3,
      WeightType: item.weightType || 2,
      DefaultWeight: assignment.weight || item.defaultWeight || 0,
      UnitName: item.unitName || "ร้อยละ",
      Description: item.description || "",
      CriteriaLevel1: item.criteriaLevel1 || "",
      CriteriaLevel2: item.criteriaLevel2 || "",
      CriteriaLevel3: item.criteriaLevel3 || "",
      CriteriaLevel4: item.criteriaLevel4 || "",
      CriteriaLevel5: item.criteriaLevel5 || "",
      Criteria12M_Level1: item.criteria12M_Level1 || "",
      Criteria12M_Level2: item.criteria12M_Level2 || "",
      Criteria12M_Level3: item.criteria12M_Level3 || "",
      Criteria12M_Level4: item.criteria12M_Level4 || "",
      Criteria12M_Level5: item.criteria12M_Level5 || "",
      CalculationFormula: item.calculationFormula || "",
      ScoreExample: item.scoreExample || "",
      BaselineData: item.baselineData || "",
      AssignedUserNames: [currentUserName]
    });
    setIsIndividualModalOpen(true);
  };

  const handleOpenWeightModal = assignment => {
    setWeightAssignment(assignment);
    setWeightInputValue(
      assignment.weight !== undefined && assignment.weight !== null
        ? String(assignment.weight)
        : ""
    );
    setIsWeightModalOpen(true);
  };

  const handleSaveWeight = async e => {
    if (e) e.preventDefault();
    if (!weightAssignment) return;

    const numWeight = parseFloat(weightInputValue);
    if (isNaN(numWeight) || numWeight < 0 || numWeight > 100) {
      alert("กรุณากรอกค่าน้ำหนักที่ถูกต้อง (ระหว่าง 0 ถึง 100)");
      return;
    }

    try {
      setIsSavingWeight(true);
      await KpiService.updateAssignmentMetadata(weightAssignment.assignmentID, {
        Weight: numWeight
      });
      setIsWeightModalOpen(false);
      setWeightAssignment(null);
      if (selectedPeriodId) {
        await fetchMySummary(selectedPeriodId);
      }
    } catch (err) {
      console.error("Error updating personal weight:", err);
      alert(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "ไม่สามารถบันทึกค่าน้ำหนักได้ กรุณาลองใหม่อีกครั้ง"
      );
    } finally {
      setIsSavingWeight(false);
    }
  };

  const handleOpenDefendModal = assignment => {
    const item = assignment.kpiItem || {};
    setActiveItem(item);
    setActiveAssignment(assignment);

    const parsed = parseDefendData(assignment.defendRemark);
    const proposed = parsed?.proposed || {};
    const reason = parsed?.reason || (typeof assignment.defendRemark === "string" ? assignment.defendRemark : "");

    setDefendFormData({
      KpiName: proposed.kpiName !== undefined ? proposed.kpiName : (item.kpiName || ""),
      TargetScoreLevel: proposed.targetScoreLevel !== undefined ? proposed.targetScoreLevel : (item.targetScoreLevel || 3),
      DefaultWeight: proposed.weight !== undefined ? proposed.weight : (assignment.weight || item.defaultWeight || 0),
      Description: proposed.description !== undefined ? proposed.description : (item.description || ""),
      CriteriaLevel1: proposed.criteriaLevel1 !== undefined ? proposed.criteriaLevel1 : (item.criteriaLevel1 || ""),
      CriteriaLevel2: proposed.criteriaLevel2 !== undefined ? proposed.criteriaLevel2 : (item.criteriaLevel2 || ""),
      CriteriaLevel3: proposed.criteriaLevel3 !== undefined ? proposed.criteriaLevel3 : (item.criteriaLevel3 || ""),
      CriteriaLevel4: proposed.criteriaLevel4 !== undefined ? proposed.criteriaLevel4 : (item.criteriaLevel4 || ""),
      CriteriaLevel5: proposed.criteriaLevel5 !== undefined ? proposed.criteriaLevel5 : (item.criteriaLevel5 || ""),
      Criteria12M_Level1: proposed.criteria12M_Level1 !== undefined ? proposed.criteria12M_Level1 : (item.criteria12M_Level1 || ""),
      Criteria12M_Level2: proposed.criteria12M_Level2 !== undefined ? proposed.criteria12M_Level2 : (item.criteria12M_Level2 || ""),
      Criteria12M_Level3: proposed.criteria12M_Level3 !== undefined ? proposed.criteria12M_Level3 : (item.criteria12M_Level3 || ""),
      Criteria12M_Level4: proposed.criteria12M_Level4 !== undefined ? proposed.criteria12M_Level4 : (item.criteria12M_Level4 || ""),
      Criteria12M_Level5: proposed.criteria12M_Level5 !== undefined ? proposed.criteria12M_Level5 : (item.criteria12M_Level5 || ""),
      CalculationFormula: proposed.calculationFormula !== undefined ? proposed.calculationFormula : (item.calculationFormula || ""),
      ScoreExample: proposed.scoreExample !== undefined ? proposed.scoreExample : (item.scoreExample || ""),
      BaselineData: proposed.baselineData !== undefined ? proposed.baselineData : (item.baselineData || ""),
      Reason: reason,
      IsSingleUserFork: false,
      TargetUserName: currentUserName
    });

    setDefendActiveTab(assignment.agreementStatus === 2 ? "compare" : "form");
    setIsDefendModalOpen(true);
  };

  const handleOpenSelfAssessModal = assignment => {
    setActiveItem(assignment.kpiItem);
    setActiveAssignment(assignment);
    setSelfAssessFormData({
      ActualAchievement: assignment.actualAchievement || "",
      SelfScoreLevel:
        assignment.selfScoreLevel || assignment.kpiItem?.targetScoreLevel || 3,
      AttachmentFileUrls: assignment.attachmentFileUrls || ""
    });
    setIsSelfAssessModalOpen(true);
  };

  const handleOpenClarifyModal = assignment => {
    setClarifyingAssignment(assignment);
    setClarificationText(assignment.subordinateClarification || "");
    setIsClarifyModalOpen(true);
  };

  const handleCloseClarifyModal = () => {
    setIsClarifyModalOpen(false);
    setClarifyingAssignment(null);
    setClarificationText("");
  };

  const handleSubmitClarification = async () => {
    if (!clarifyingAssignment) return;
    if (!clarificationText.trim()) {
      alert("กรุณาระบุคำชี้แจงหรือข้อมูลตอบกลับก่อนกดยืนยัน");
      return;
    }

    setIsSubmittingClarification(true);
    try {
      await KpiService.clarifyAssignment(clarifyingAssignment.assignmentID, {
        clarification: clarificationText.trim()
      });
      alert(
        "ส่งคำชี้แจงเรียบร้อยแล้ว!\nระบบได้ปรับสถานะตัวชี้วัดกลับมาเป็น 'รออนุมัติ' และแจ้งเตือนให้หัวหน้างานพิจารณาแล้ว"
      );
      handleCloseClarifyModal();
      fetchMySummary(selectedPeriodId);
    } catch (error) {
      console.error("Error submitting clarification:", error);
      alert(
        "เกิดข้อผิดพลาดในการส่งคำชี้แจง: " +
          (error.response?.data?.errors?.[0]?.message ||
            error.response?.data?.message ||
            error.message)
      );
    } finally {
      setIsSubmittingClarification(false);
    }
  };

  // --- Handlers: Actions ---
  const handleAcceptAgreement = async assignment => {
    if (
      window.confirm(
        `คุณต้องการยืนยัน "ยอมรับข้อตกลง" ตัวชี้วัด: ${assignment.kpiItem?.kpiName} หรือไม่?`
      )
    ) {
      setIsSaving(true);
      try {
        await KpiService.respondAgreement(assignment.assignmentID, {
          AgreementStatus: 1, // Accepted
          DefendRemark: "ยอมรับข้อตกลงตัวชี้วัด"
        });
        alert("บันทึกการยอมรับข้อตกลงสำเร็จ!");
        fetchMySummary(selectedPeriodId);
      } catch (error) {
        alert(
          "เกิดข้อผิดพลาด: " +
            (error.response?.data?.errors?.[0]?.message || error.message)
        );
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleSaveIndividualKpi = async e => {
    e.preventDefault();
    if (!individualFormData.KpiName.trim()) {
      alert("กรุณากรอกชื่อตัวชี้วัด");
      return;
    }

    setIsSaving(true);
    try {
      if (editingItemId) {
        await KpiService.updateItem(editingItemId, individualFormData);
        // Also update assignment weight if changed
        if (activeAssignment && activeAssignment.assignmentID) {
          await KpiService.updateAssignmentMetadata(
            activeAssignment.assignmentID,
            {
              Weight: individualFormData.DefaultWeight
            }
          );
        }
        alert("แก้ไขตัวชี้วัดส่วนบุคคลสำเร็จ!");
      } else {
        await KpiService.createItem({
          ...individualFormData,
          AssignedUserNames: [currentUserName]
        });
        alert("สร้างตัวชี้วัดส่วนบุคคลสำเร็จ!");
      }
      setIsIndividualModalOpen(false);
      fetchMySummary(selectedPeriodId);
    } catch (error) {
      alert(
        "เกิดข้อผิดพลาด: " +
          (error.response?.data?.errors?.[0]?.message || error.message)
      );
    } finally {
      setIsSaving(false);
    }
  };

  const getComparisonRows = () => {
    if (!activeItem) return [];
    const orig = {
      kpiName: activeItem.kpiName || "",
      targetScoreLevel: activeItem.targetScoreLevel || 3,
      weight:
        activeAssignment?.weight != null
          ? activeAssignment.weight
          : activeItem.defaultWeight || 0,
      description: activeItem.description || "",
      criteriaLevel1: activeItem.criteriaLevel1 || "",
      criteriaLevel2: activeItem.criteriaLevel2 || "",
      criteriaLevel3: activeItem.criteriaLevel3 || "",
      criteriaLevel4: activeItem.criteriaLevel4 || "",
      criteriaLevel5: activeItem.criteriaLevel5 || "",
      criteria12M_Level1: activeItem.criteria12M_Level1 || "",
      criteria12M_Level2: activeItem.criteria12M_Level2 || "",
      criteria12M_Level3: activeItem.criteria12M_Level3 || "",
      criteria12M_Level4: activeItem.criteria12M_Level4 || "",
      criteria12M_Level5: activeItem.criteria12M_Level5 || "",
      calculationFormula: activeItem.calculationFormula || "",
      scoreExample: activeItem.scoreExample || "",
      baselineData: activeItem.baselineData || ""
    };

    const is12M = Boolean(
      currentPeriod?.kpiRound === 2 ||
        activeItem?.kpiPeriod?.kpiRound === 2 ||
        orig.criteria12M_Level1 ||
        orig.criteria12M_Level2 ||
        orig.criteria12M_Level3 ||
        defendFormData.Criteria12M_Level1 ||
        defendFormData.Criteria12M_Level3
    );

    const isFixedWeight = activeItem?.weightType === 1;

    const rows = [
      {
        id: "kpiName",
        field: "ชื่อตัวชี้วัด",
        origDisplay: orig.kpiName || <span className="text-muted font-italic">- ไม่ได้ระบุ -</span>,
        origRaw: String(orig.kpiName || ""),
        propRaw: String(defendFormData.KpiName || ""),
        renderInput: disabled => (
          <input
            type="text"
            className="form-control form-control-sm"
            value={defendFormData.KpiName}
            disabled={disabled}
            onChange={e =>
              setDefendFormData({ ...defendFormData, KpiName: e.target.value })
            }
            placeholder="ชื่อตัวชี้วัดที่ขอปรับ..."
          />
        )
      },
      {
        id: "weight",
        field: "% น้ำหนักคะแนน",
        origDisplay: (
          <div>
            <span className="font-weight-bold text-success">{orig.weight}%</span>
            {isFixedWeight && (
              <span className="badge badge-secondary ml-2" style={{ fontSize: "10px" }}>น้ำหนักบังคับ</span>
            )}
          </div>
        ),
        origRaw: String(orig.weight ?? 0),
        propRaw: String(defendFormData.DefaultWeight ?? 0),
        renderInput: disabled => {
          if (isFixedWeight) {
            return (
              <div className="d-flex align-items-center">
                <span className="font-weight-bold text-muted mr-2">{orig.weight}%</span>
                <span className="badge badge-secondary" style={{ fontSize: "11px" }}>
                  น้ำหนักบังคับ (ทุกคนใช้เท่ากัน)
                </span>
              </div>
            );
          }
          const maxAllowed = activeItem?.defaultWeight || 100;
          return (
            <div>
              <div className="input-group input-group-sm" style={{ maxWidth: "160px" }}>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max={maxAllowed}
                  className="form-control font-weight-bold text-success"
                  value={defendFormData.DefaultWeight}
                  disabled={disabled}
                  onChange={e =>
                    setDefendFormData({
                      ...defendFormData,
                      DefaultWeight: parseFloat(e.target.value) || 0
                    })
                  }
                />
                <div className="input-group-append">
                  <span className="input-group-text">%</span>
                </div>
              </div>
              <small className="text-muted d-block mt-1">
                ค่าน้ำหนักสูงสุดที่ใช้ได้คือ {maxAllowed}%
              </small>
            </div>
          );
        }
      },
      {
        id: "description",
        field: "คำอธิบายตัวชี้วัด",
        origDisplay: orig.description || <span className="text-muted font-italic">- ไม่มีข้อมูล -</span>,
        origRaw: String(orig.description || ""),
        propRaw: String(defendFormData.Description || ""),
        renderInput: disabled => (
          <textarea
            rows={2}
            className="form-control form-control-sm"
            value={defendFormData.Description}
            disabled={disabled}
            placeholder="คำอธิบายตัวชี้วัด..."
            onChange={e =>
              setDefendFormData({
                ...defendFormData,
                Description: e.target.value
              })
            }
          />
        )
      },
      {
        id: "header_6m",
        isHeader: true,
        headerTitle: "เกณฑ์รอบ 6 เดือน",
        icon: "fa-calendar-alt",
        color: "#0369a1",
        bg: "#e0f2fe"
      },
      {
        id: "criteriaLevel1",
        field: "เกณฑ์ระดับ 1",
        origDisplay: orig.criteriaLevel1 || <span className="text-muted font-italic">- ไม่มีข้อมูล -</span>,
        origRaw: String(orig.criteriaLevel1 || ""),
        propRaw: String(defendFormData.CriteriaLevel1 || ""),
        renderInput: disabled => (
          <textarea
            rows={2}
            className="form-control form-control-sm"
            value={defendFormData.CriteriaLevel1}
            disabled={disabled}
            placeholder="เกณฑ์การประเมินระดับ 1..."
            onChange={e =>
              setDefendFormData({
                ...defendFormData,
                CriteriaLevel1: e.target.value
              })
            }
          />
        )
      },
      {
        id: "criteriaLevel2",
        field: "เกณฑ์ระดับ 2",
        origDisplay: orig.criteriaLevel2 || <span className="text-muted font-italic">- ไม่มีข้อมูล -</span>,
        origRaw: String(orig.criteriaLevel2 || ""),
        propRaw: String(defendFormData.CriteriaLevel2 || ""),
        renderInput: disabled => (
          <textarea
            rows={2}
            className="form-control form-control-sm"
            value={defendFormData.CriteriaLevel2}
            disabled={disabled}
            placeholder="เกณฑ์การประเมินระดับ 2..."
            onChange={e =>
              setDefendFormData({
                ...defendFormData,
                CriteriaLevel2: e.target.value
              })
            }
          />
        )
      },
      {
        id: "criteriaLevel3",
        field: "เกณฑ์ระดับ 3 (เป้าหมาย)",
        origDisplay: orig.criteriaLevel3 || <span className="text-muted font-italic">- ไม่มีข้อมูล -</span>,
        origRaw: String(orig.criteriaLevel3 || ""),
        propRaw: String(defendFormData.CriteriaLevel3 || ""),
        renderInput: disabled => (
          <textarea
            rows={2}
            className="form-control form-control-sm"
            value={defendFormData.CriteriaLevel3}
            disabled={disabled}
            placeholder="เกณฑ์การประเมินระดับ 3 (เป้าหมาย)..."
            onChange={e =>
              setDefendFormData({
                ...defendFormData,
                CriteriaLevel3: e.target.value
              })
            }
            required
          />
        )
      },
      {
        id: "criteriaLevel4",
        field: "เกณฑ์ระดับ 4",
        origDisplay: orig.criteriaLevel4 || <span className="text-muted font-italic">- ไม่มีข้อมูล -</span>,
        origRaw: String(orig.criteriaLevel4 || ""),
        propRaw: String(defendFormData.CriteriaLevel4 || ""),
        renderInput: disabled => (
          <textarea
            rows={2}
            className="form-control form-control-sm"
            value={defendFormData.CriteriaLevel4}
            disabled={disabled}
            placeholder="เกณฑ์การประเมินระดับ 4..."
            onChange={e =>
              setDefendFormData({
                ...defendFormData,
                CriteriaLevel4: e.target.value
              })
            }
          />
        )
      },
      {
        id: "criteriaLevel5",
        field: "เกณฑ์ระดับ 5",
        origDisplay: orig.criteriaLevel5 || <span className="text-muted font-italic">- ไม่มีข้อมูล -</span>,
        origRaw: String(orig.criteriaLevel5 || ""),
        propRaw: String(defendFormData.CriteriaLevel5 || ""),
        renderInput: disabled => (
          <textarea
            rows={2}
            className="form-control form-control-sm"
            value={defendFormData.CriteriaLevel5}
            disabled={disabled}
            placeholder="เกณฑ์การประเมินระดับ 5..."
            onChange={e =>
              setDefendFormData({
                ...defendFormData,
                CriteriaLevel5: e.target.value
              })
            }
          />
        )
      }
    ];

    if (is12M) {
      rows.push(
        {
          id: "header_12m",
          isHeader: true,
          headerTitle: "เกณฑ์รอบ 12 เดือน",
          icon: "fa-calendar-check",
          color: "#15803d",
          bg: "#f0fdf4"
        },
        {
          id: "criteria12M_Level1",
          field: "เกณฑ์ระดับ 1",
          origDisplay: orig.criteria12M_Level1 || <span className="text-muted font-italic">- ไม่มีข้อมูล -</span>,
          origRaw: String(orig.criteria12M_Level1 || ""),
          propRaw: String(defendFormData.Criteria12M_Level1 || ""),
          renderInput: disabled => (
            <textarea
              rows={2}
              className="form-control form-control-sm"
              value={defendFormData.Criteria12M_Level1}
              disabled={disabled}
              placeholder="เกณฑ์ระดับ 1..."
              onChange={e =>
                setDefendFormData({
                  ...defendFormData,
                  Criteria12M_Level1: e.target.value
                })
              }
            />
          )
        },
        {
          id: "criteria12M_Level2",
          field: "เกณฑ์ระดับ 2",
          origDisplay: orig.criteria12M_Level2 || <span className="text-muted font-italic">- ไม่มีข้อมูล -</span>,
          origRaw: String(orig.criteria12M_Level2 || ""),
          propRaw: String(defendFormData.Criteria12M_Level2 || ""),
          renderInput: disabled => (
            <textarea
              rows={2}
              className="form-control form-control-sm"
              value={defendFormData.Criteria12M_Level2}
              disabled={disabled}
              placeholder="เกณฑ์ระดับ 2..."
              onChange={e =>
                setDefendFormData({
                  ...defendFormData,
                  Criteria12M_Level2: e.target.value
                })
              }
            />
          )
        },
        {
          id: "criteria12M_Level3",
          field: "เกณฑ์ระดับ 3",
          origDisplay: orig.criteria12M_Level3 || <span className="text-muted font-italic">- ไม่มีข้อมูล -</span>,
          origRaw: String(orig.criteria12M_Level3 || ""),
          propRaw: String(defendFormData.Criteria12M_Level3 || ""),
          renderInput: disabled => (
            <textarea
              rows={2}
              className="form-control form-control-sm"
              value={defendFormData.Criteria12M_Level3}
              disabled={disabled}
              placeholder="เกณฑ์ระดับ 3..."
              onChange={e =>
                setDefendFormData({
                  ...defendFormData,
                  Criteria12M_Level3: e.target.value
                })
              }
            />
          )
        },
        {
          id: "criteria12M_Level4",
          field: "เกณฑ์ระดับ 4",
          origDisplay: orig.criteria12M_Level4 || <span className="text-muted font-italic">- ไม่มีข้อมูล -</span>,
          origRaw: String(orig.criteria12M_Level4 || ""),
          propRaw: String(defendFormData.Criteria12M_Level4 || ""),
          renderInput: disabled => (
            <textarea
              rows={2}
              className="form-control form-control-sm"
              value={defendFormData.Criteria12M_Level4}
              disabled={disabled}
              placeholder="เกณฑ์ระดับ 4..."
              onChange={e =>
                setDefendFormData({
                  ...defendFormData,
                  Criteria12M_Level4: e.target.value
                })
              }
            />
          )
        },
        {
          id: "criteria12M_Level5",
          field: "เกณฑ์ระดับ 5",
          origDisplay: orig.criteria12M_Level5 || <span className="text-muted font-italic">- ไม่มีข้อมูล -</span>,
          origRaw: String(orig.criteria12M_Level5 || ""),
          propRaw: String(defendFormData.Criteria12M_Level5 || ""),
          renderInput: disabled => (
            <textarea
              rows={2}
              className="form-control form-control-sm"
              value={defendFormData.Criteria12M_Level5}
              disabled={disabled}
              placeholder="เกณฑ์ระดับ 5..."
              onChange={e =>
                setDefendFormData({
                  ...defendFormData,
                  Criteria12M_Level5: e.target.value
                })
              }
            />
          )
        }
      );
    }


    return rows;
  };

  const handleSubmitDefend = async e => {
    e.preventDefault();
    if (stage >= 2) {
      alert("สิ้นสุดช่วงเวลาการขอ Defend แล้ว (ขณะนี้เป็นช่วงเวลาที่คณะกรรมการพิจารณาและอนุมัติ) ไม่สามารถแก้ไขหรือส่งคำขอเพิ่มเติมได้");
      return;
    }
    if (!defendFormData.Reason.trim()) {
      alert("กรุณาระบุเหตุผลความจำเป็นในการขอ Defend ปรับเกณฑ์");
      return;
    }
    if (activeItem?.weightType === 2) {
      const maxAllowed = activeItem?.defaultWeight || 100;
      const proposedWeight = parseFloat(defendFormData.DefaultWeight) || 0;
      if (proposedWeight > maxAllowed) {
        alert(`ค่าน้ำหนักที่ขอปรับต้องไม่เกินค่าน้ำหนักสูงสุด (${maxAllowed}%)`);
        return;
      }
    }

    setIsSaving(true);
    try {
      const original = {
        kpiName: activeItem?.kpiName || "",
        targetScoreLevel: activeItem?.targetScoreLevel || 3,
        weight: activeAssignment?.weight || activeItem?.defaultWeight || 0,
        description: activeItem?.description || "",
        criteriaLevel1: activeItem?.criteriaLevel1 || "",
        criteriaLevel2: activeItem?.criteriaLevel2 || "",
        criteriaLevel3: activeItem?.criteriaLevel3 || "",
        criteriaLevel4: activeItem?.criteriaLevel4 || "",
        criteriaLevel5: activeItem?.criteriaLevel5 || "",
        criteria12M_Level1: activeItem?.criteria12M_Level1 || "",
        criteria12M_Level2: activeItem?.criteria12M_Level2 || "",
        criteria12M_Level3: activeItem?.criteria12M_Level3 || "",
        criteria12M_Level4: activeItem?.criteria12M_Level4 || "",
        criteria12M_Level5: activeItem?.criteria12M_Level5 || "",
        calculationFormula: activeItem?.calculationFormula || "",
        scoreExample: activeItem?.scoreExample || "",
        baselineData: activeItem?.baselineData || ""
      };

      const proposed = {
        kpiName: defendFormData.KpiName,
        targetScoreLevel: parseInt(defendFormData.TargetScoreLevel, 10) || 3,
        weight: parseFloat(defendFormData.DefaultWeight) || 0,
        description: defendFormData.Description,
        criteriaLevel1: defendFormData.CriteriaLevel1,
        criteriaLevel2: defendFormData.CriteriaLevel2,
        criteriaLevel3: defendFormData.CriteriaLevel3,
        criteriaLevel4: defendFormData.CriteriaLevel4,
        criteriaLevel5: defendFormData.CriteriaLevel5,
        criteria12M_Level1: defendFormData.Criteria12M_Level1,
        criteria12M_Level2: defendFormData.Criteria12M_Level2,
        criteria12M_Level3: defendFormData.Criteria12M_Level3,
        criteria12M_Level4: defendFormData.Criteria12M_Level4,
        criteria12M_Level5: defendFormData.Criteria12M_Level5,
        calculationFormula: activeItem?.calculationFormula || "",
        scoreExample: activeItem?.scoreExample || "",
        baselineData: activeItem?.baselineData || ""
      };

      const defendPayload = JSON.stringify({
        reason: defendFormData.Reason.trim(),
        defendedAt: new Date().toISOString(),
        defendedBy: currentUserName,
        original,
        proposed
      });

      if (activeAssignment && activeAssignment.assignmentID) {
        await KpiService.respondAgreement(activeAssignment.assignmentID, {
          AgreementStatus: 2,
          DefendRemark: defendPayload
        });
      } else {
        await KpiService.defendItem(activeItem.kpiItemID, {
          ...defendFormData,
          Reason: defendPayload,
          TargetUserName: currentUserName
        });
      }
      alert(
        "ส่งคำขอ Defend สำเร็จ! ระบบได้บันทึกข้อมูลเปรียบเทียบก่อน-หลัง เพื่อให้คณะกรรมการพิจารณาเรียบร้อยแล้ว"
      );
      setIsDefendModalOpen(false);
      fetchMySummary(selectedPeriodId);
    } catch (error) {
      alert(
        "เกิดข้อผิดพลาดในการ Defend: " +
          (error.response?.data?.errors?.[0]?.message || error.message)
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitSelfAssessment = async e => {
    e.preventDefault();
    if (!selfAssessFormData.ActualAchievement.trim()) {
      alert("กรุณากรอกผลการดำเนินงานจริง (Actual Achievement)");
      return;
    }

    setIsSaving(true);
    try {
      await KpiService.submitSelfAssessment(activeAssignment.assignmentID, {
        ActualAchievement: selfAssessFormData.ActualAchievement,
        SelfScoreLevel: parseInt(selfAssessFormData.SelfScoreLevel, 10),
        AttachmentFileUrls: selfAssessFormData.AttachmentFileUrls
      });
      alert("บันทึกและส่งผลการประเมินตนเองสำเร็จ!");
      setIsSelfAssessModalOpen(false);
      fetchMySummary(selectedPeriodId);
    } catch (error) {
      alert(
        "เกิดข้อผิดพลาด: " +
          (error.response?.data?.errors?.[0]?.message || error.message)
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteIndividualKpi = async assignment => {
    if (
      window.confirm(
        `คุณแน่ใจหรือไม่ว่าต้องการลบตัวชี้วัดส่วนบุคคล: "${assignment.kpiItem?.kpiName}"?`
      )
    ) {
      setIsSaving(true);
      try {
        await KpiService.deleteItem(assignment.kpiItem?.kpiItemID);
        alert("ลบตัวชี้วัดสำเร็จ!");
        fetchMySummary(selectedPeriodId);
      } catch (error) {
        alert("เกิดข้อผิดพลาดในการลบ: " + error.message);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleCommitKpi = async () => {
    if (!selectedPeriodId) return;

    if (displayedAssignments.length === 0) {
      alert("ไม่พบรายการตัวชี้วัดในรอบการประเมินนี้ กรุณาเพิ่มหรือเลือกตัวชี้วัดก่อน");
      return;
    }

    if (!isDisplayedWeightValid) {
      alert(
        `ผลรวมน้ำหนักของตัวชี้วัดทั้งหมดต้องเท่ากับ 100% พอดี\nปัจจุบันรวมได้ ${displayedTotalWeight}%\nกรุณาปรับน้ำหนักก่อนยืนยันส่ง`
      );
      return;
    }

    const confirmMsg =
      `ยืนยันการส่งชุดตัวชี้วัด (Commit KPI)?\n\n` +
      `- จำนวนตัวชี้วัด: ${totalKpiCount} ข้อ\n` +
      `- น้ำหนักรวม: ${displayedTotalWeight}%\n\n` +
      `เมื่อยืนยันส่งแล้ว ระบบจะล็อกการแก้ไข/ลบรายการทั้งหมด เพื่อส่งให้ผู้บังคับบัญชาตามลำดับชั้นพิจารณาอนุมัติต่อไป\n\n` +
      `คุณต้องการดำเนินการต่อหรือไม่?`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setIsCommitting(true);
    try {
      await KpiService.commitMyKpi(selectedPeriodId);
      alert("ยืนยันส่งตัวชี้วัด (Commit KPI) เรียบร้อยแล้ว!\nระบบได้ส่งข้อมูลให้ผู้บังคับบัญชาพิจารณาอนุมัติแล้ว");
      fetchMySummary(selectedPeriodId);
    } catch (error) {
      console.error("Error committing KPI:", error);
      const errMsg =
        error.response?.data?.errors?.[0]?.message ||
        error.response?.data?.message ||
        error.message;
      alert("เกิดข้อผิดพลาดในการยืนยันส่งตัวชี้วัด: " + errMsg);
    } finally {
      setIsCommitting(false);
    }
  };

  const getTimelineBadge = stage => {
    switch (stage) {
      case 0:
        return {
          color: "badge-secondary",
          name: "ช่วงเตรียมการ / จัดทำตัวชี้วัด (Pre-Setup)"
        };
      case 1:
        return {
          color: "badge-primary",
          name: "ช่วงที่ 1: จัดทำตัวชี้วัด (Setup)"
        };
      case 2:
        return {
          color: "badge-warning text-dark",
          name: "ช่วงที่ 2: Defend & ยอมรับข้อตกลง (Agreement)"
        };
      case 3:
        return {
          color: "badge-info",
          name: "ช่วงที่ 3: ประเมินตนเอง (Self Assessment)"
        };
      case 4:
        return {
          color: "badge-success",
          name: "ช่วงที่ 4: กลั่นกรองสรุปคะแนน (Review & Evaluation)"
        };
      case 5:
        return { color: "badge-dark", name: "สิ้นสุดรอบการประเมินแล้ว" };
      default:
        return {
          color: "badge-secondary",
          name: "ช่วงเตรียมการจัดทำตัวชี้วัด"
        };
    }
  };

  const currentPeriod = periods.find(p => p.periodID === selectedPeriodId);
  const stage = summaryData?.currentTimelineStage ?? 0;
  // Can create/edit individual KPI during Pre-setup (0), Setup (1), or Defend (2)
  const canManageIndividual = stage <= 2;
  const isSelfAssessPeriod = stage === 3;
  const isReviewPeriod = stage >= 4;

  // แสดงตัวชี้วัดทั้งหมดที่ได้รับมอบหมาย โดยรองรับการเรียงลำดับแบบกำหนดเอง (Custom Reorder) และเรียงตามคอลัมน์ (Sorting)
  const displayedAssignments = useMemo(() => {
    if (!localAssignments || localAssignments.length === 0) return [];

    const list = [...localAssignments];

    if (sortField === "custom") {
      return sortOrder === "desc" ? list.reverse() : list;
    }

    return list.sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case "type":
          valA = a.kpiItem?.kpiType || 0;
          valB = b.kpiItem?.kpiType || 0;
          break;
        case "code":
          valA = (a.kpiItem?.code || "").toLowerCase();
          valB = (b.kpiItem?.code || "").toLowerCase();
          return sortOrder === "asc"
            ? valA.localeCompare(valB, undefined, { numeric: true })
            : valB.localeCompare(valA, undefined, { numeric: true });
        case "name":
          valA = (a.kpiItem?.kpiName || "").toLowerCase();
          valB = (b.kpiItem?.kpiName || "").toLowerCase();
          return sortOrder === "asc"
            ? valA.localeCompare(valB, "th")
            : valB.localeCompare(valA, "th");
        case "target":
          valA = a.kpiItem?.targetScoreLevel || 0;
          valB = b.kpiItem?.targetScoreLevel || 0;
          break;
        case "weight":
          valA = Number(a.weight) || 0;
          valB = Number(b.weight) || 0;
          break;
        case "itemStatus":
          valA = a.kpiItem?.status || 0;
          valB = b.kpiItem?.status || 0;
          break;
        case "assignmentStatus":
          valA = a.assignmentStatus || 0;
          valB = b.assignmentStatus || 0;
          break;
        default:
          return 0;
      }

      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [localAssignments, sortField, sortOrder]);

  const handleSort = field => {
    if (sortField === field) {
      setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getSortIcon = field => {
    if (sortField !== field) {
      return (
        <i
          className="fas fa-sort text-muted ml-1"
          style={{ opacity: 0.35, fontSize: "11px" }}
        />
      );
    }
    return sortOrder === "asc" ? (
      <i
        className="fas fa-sort-up text-primary ml-1"
        style={{ fontSize: "11px" }}
      />
    ) : (
      <i
        className="fas fa-sort-down text-primary ml-1"
        style={{ fontSize: "11px" }}
      />
    );
  };

  const handleMoveOrder = async (index, direction) => {
    if (!selectedPeriodId || isReordering) return;
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= displayedAssignments.length) return;

    // สลับตำแหน่งตามที่กำลังแสดงผล
    const newList = [...displayedAssignments];
    const temp = newList[index];
    newList[index] = newList[targetIndex];
    newList[targetIndex] = temp;

    // อัปเดต state หน้าจอทันที
    setLocalAssignments(newList);
    setSortField("custom");
    setSortOrder("asc");

    // บันทึกลำดับใหม่ลง Database ผ่าน Backend API
    setIsReordering(true);
    try {
      const assignmentIds = newList.map(a => a.assignmentID);
      await KpiService.reorderMyAssignments(selectedPeriodId, assignmentIds);
    } catch (error) {
      console.error("Error saving KPI order:", error);
      alert(
        "เกิดข้อผิดพลาดในการบันทึกลำดับตัวชี้วัด: " +
          (error.response?.data?.message || error.message)
      );
      if (summaryData?.assignments) {
        setLocalAssignments(summaryData.assignments);
      }
    } finally {
      setIsReordering(false);
    }
  };

  const handleSaveCurrentOrderAsCustom = async () => {
    if (!selectedPeriodId || isReordering || displayedAssignments.length === 0)
      return;
    setIsReordering(true);
    try {
      const newList = [...displayedAssignments];
      const assignmentIds = newList.map(a => a.assignmentID);
      await KpiService.reorderMyAssignments(selectedPeriodId, assignmentIds);
      setLocalAssignments(newList);
      setSortField("custom");
      setSortOrder("asc");
      alert("บันทึกลำดับตัวชี้วัดเรียบร้อยแล้ว");
    } catch (error) {
      console.error("Error saving custom order:", error);
      alert(
        "เกิดข้อผิดพลาดในการบันทึกลำดับ: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setIsReordering(false);
    }
  };

  const displayedTotalWeight = useMemo(() => {
    return displayedAssignments.reduce(
      (sum, a) => sum + (Number(a.weight) || 0),
      0
    );
  }, [displayedAssignments]);

  const isDisplayedWeightValid = Math.abs(displayedTotalWeight - 100) < 0.01;

  const totalKpiCount = displayedAssignments.length;
  const approvedKpiCount =
    displayedAssignments.filter(
      a =>
        a.isFullyApproved ||
        Boolean(a.missionApprovedBy) ||
        a.assignmentStatus === 2
    ).length || 0;
  const pendingKpiCount = totalKpiCount - approvedKpiCount;

  const isCommitted = summaryData?.isCommitted || false;
  const canEdit = summaryData?.canEdit ?? (!isCommitted && stage <= 2);
  const canCommit = summaryData?.canCommit ?? (isDisplayedWeightValid && !isCommitted && totalKpiCount > 0);
  const overallStatus = summaryData?.overallStatus || "";

  return (
    <div>
      {/* --- Filter & Header Bar --- */}
      <Grid>
        <Col xs={12} sm={12} md={12}>
          <Card>
            <CardHeader
              //color="primary"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                backgroundColor:"#007bff"
              }}
            >
              <div>
                <h4
                  className={classes.cardTitleWhite}
                  style={{ margin: 0, fontWeight: "bold" }}
                >
                  <i className="fas fa-user-check mr-2"></i>{" "}
                  ตัวชี้วัดของฉันและการประเมินตนเอง (My KPI & Self-Assessment)
                </h4>
                <p
                  style={{
                    color: "rgba(255,255,255,0.85)",
                    margin: "4px 0 0 0",
                    fontSize: "14px"
                  }}
                >
                  ผู้ใช้งาน:{" "}
                  <strong>{currentUserName || "ผู้รับการประเมิน"}</strong> |
                  ตรวจสอบตัวชี้วัดที่ได้รับมอบหมาย, กดยอมรับ/ขอ Defend, เพิ่ม
                  KPI ส่วนบุคคล และบันทึกผลการประเมิน
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  marginTop: "8px",
                  flexWrap: "wrap"
                }}
              >
                {/* เลือกรอบการประเมิน Dropdown */}
                <div style={{ minWidth: "260px" }}>
                  <select
                    className="form-control"
                    value={selectedPeriodId}
                    onChange={e => setSelectedPeriodId(e.target.value)}
                    style={{
                      height: "38px",
                      fontWeight: "bold",
                      backgroundColor: "#ffffff",
                      color: "#333"
                    }}
                  >
                    {periods.length === 0 && (
                      <option value="">-- ไม่พบรอบการประเมิน --</option>
                    )}
                    {periods.map(p => (
                      <option key={p.periodID} value={p.periodID}>
                        {p.periodName} ({p.fiscalYear})
                      </option>
                    ))}
                  </select>
                </div>

                {/* ปุ่มเพิ่มตัวชี้วัดของฉัน */}
                <Button
                  color="success"
                  size="sm"
                  onClick={handleOpenCreateIndividualModal}
                  disabled={!selectedPeriodId || !canEdit}
                  title={
                    !canEdit
                      ? "ล็อกการแก้ไขเนื่องจากส่งตัวชี้วัดแล้ว"
                      : "เพิ่มตัวชี้วัดผลงานของฉัน (Individual KPI)"
                  }
                >
                  <i className="fas fa-plus mr-1"></i> เพิ่มตัวชี้วัดของฉัน
                  (Individual KPI)
                </Button>

                {/* ปุ่มยืนยันส่งตัวชี้วัด (Commit KPI) */}
                <Button
                  color={isCommitted ? "info" : isDisplayedWeightValid && totalKpiCount > 0 ? "rose" : "secondary"}
                  size="sm"
                  onClick={handleCommitKpi}
                  disabled={!canCommit || isCommitting}
                  title={
                    isCommitted
                      ? "ส่งตัวชี้วัดเรียบร้อยแล้ว (รอการอนุมัติ)"
                      : !isDisplayedWeightValid
                      ? "น้ำหนักรวมต้องครบ 100% จึงจะสามารถส่งได้"
                      : "ยืนยันส่งชุดตัวชี้วัดให้หัวหน้างานพิจารณาอนุมัติ"
                  }
                >
                  {isCommitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-1"></i> กำลังส่ง...
                    </>
                  ) : isCommitted ? (
                    <>
                      <i className="fas fa-check-circle mr-1"></i> ยืนยันส่งแล้ว (Committed)
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane mr-1"></i> ยืนยันส่งตัวชี้วัด (Commit KPI)
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>

            <CardBody>
              {/* Status Alert Banner */}
              {summaryData && (
                <div className="mb-3">
                  {overallStatus === "ส่งกลับแก้ไข" ? (
                    <div
                      className="alert alert-danger d-flex align-items-center mb-0 shadow-sm"
                      style={{ borderRadius: "8px", borderLeft: "5px solid #dc3545" }}
                    >
                      <i className="fas fa-exclamation-triangle fa-2x mr-3 text-danger"></i>
                      <div>
                        <strong style={{ fontSize: "15px" }}>ผู้บังคับบัญชาส่งกลับชุดตัวชี้วัดให้ท่านแก้ไข</strong>
                        <div className="mt-1" style={{ fontSize: "13px" }}>
                          {summaryData.latestRejectRemark ? (
                            <span>
                              <strong>ข้อเสนอแนะ/เหตุผล:</strong> {summaryData.latestRejectRemark}
                            </span>
                          ) : (
                            "กรุณาปรับปรุงรายการและน้ำหนักตัวชี้วัดให้ถูกต้อง จากนั้นกดปุ่ม 'ยืนยันส่งตัวชี้วัด (Commit KPI)' อีกครั้ง"
                          )}
                        </div>
                      </div>
                    </div>
                  ) : isCommitted ? (
                    <div
                      className="alert alert-info d-flex align-items-center mb-0 shadow-sm"
                      style={{
                        borderRadius: "8px",
                        backgroundColor: "#eff6ff",
                        borderColor: "#bfdbfe",
                        color: "#1e40af",
                        borderLeft: "5px solid #3b82f6"
                      }}
                    >
                      <i className="fas fa-lock fa-2x mr-3 text-primary"></i>
                      <div>
                        <strong style={{ fontSize: "15px" }}>
                          {approvedKpiCount === totalKpiCount && totalKpiCount > 0
                            ? "ตัวชี้วัดของท่านได้รับการอนุมัติครบถ้วนแล้ว"
                            : "ท่านได้ยืนยันส่งตัวชี้วัด (Commit KPI) เรียบร้อยแล้ว"}
                        </strong>
                        <div className="mt-1" style={{ fontSize: "13px" }}>
                          {summaryData.committedDate && (
                            <span className="mr-2">
                              ส่งเมื่อ: {moment(summaryData.committedDate).format("D MMMM YYYY เวลา HH:mm น.")} |
                            </span>
                          )}
                          {approvedKpiCount === totalKpiCount && totalKpiCount > 0
                            ? " กระบวนการจัดทำตัวชี้วัดรอบนี้เสร็จสมบูรณ์ พร้อมสำหรับการประเมินผลตนเองในรอบถัดไป"
                            : " ขณะนี้ระบบล็อกการแก้ไขเพื่อรอผู้บังคับบัญชาตามลำดับชั้นพิจารณาอนุมัติ"}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="alert alert-warning d-flex align-items-center mb-0 shadow-sm"
                      style={{
                        borderRadius: "8px",
                        backgroundColor: "#fffbeb",
                        borderColor: "#fde68a",
                        color: "#92400e",
                        borderLeft: "5px solid #f59e0b"
                      }}
                    >
                      <i className="fas fa-edit fa-2x mr-3 text-warning"></i>
                      <div>
                        <strong style={{ fontSize: "15px" }}>อยู่ระหว่างการจัดทำตัวชี้วัด (ร่าง / Draft)</strong>
                        <div className="mt-1" style={{ fontSize: "13px" }}>
                          ท่านสามารถเพิ่ม ลบ และปรับน้ำหนักตัวชี้วัดได้อย่างอิสระ{" "}
                          {isDisplayedWeightValid ? (
                            <span className="text-success font-weight-bold">
                              (น้ำหนักครบ 100% แล้ว สามารถกดปุ่ม <strong>&quot;ยืนยันส่งตัวชี้วัด (Commit KPI)&quot;</strong> ด้านบนเพื่อส่งให้หัวหน้างานได้ทันที)
                            </span>
                          ) : (
                            <span className="text-danger font-weight-bold">
                              (น้ำหนักรวมปัจจุบัน: {displayedTotalWeight}% - กรุณาปรับให้ครบ 100% จึงจะสามารถยืนยันส่งได้)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Timeline Stage Tracker Bar */}
              {currentPeriod && (
                <div
                  className="p-3 mb-3 rounded"
                  style={{
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e2e8f0"
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
                    <div>
                      <strong style={{ fontSize: "16px" }}>
                        <i className="fas fa-calendar-alt text-primary mr-2"></i>
                        {currentPeriod.periodName}
                      </strong>
                      <span
                        className={`ml-2 badge ${
                          getTimelineBadge(stage).color
                        } p-2`}
                        style={{ fontSize: "12px" }}
                      >
                        {summaryData?.currentTimelineName ||
                          getTimelineBadge(stage).name}
                      </span>
                    </div>

                    <div className="text-muted" style={{ fontSize: "13px" }}>
                      ระยะเวลารอบ:{" "}
                      {moment(currentPeriod.startDate).format("D MMM BB")} -{" "}
                      {moment(currentPeriod.endDate).format("D MMM BB")}
                    </div>
                  </div>

                  {/* 4 Timelines Stepper / Badges */}
                  <div
                    className="row text-center mt-2"
                    style={{ fontSize: "12px" }}
                  >
                    <div
                      className={`col-md-3 col-6 mb-1 ${
                        stage === 1
                          ? "font-weight-bold text-primary"
                          : "text-muted"
                      }`}
                    >
                      <div
                        className={`p-2 rounded border ${
                          stage === 1 ? "bg-light border-primary shadow-sm" : ""
                        }`}
                      >
                        1. จัดทำตัวชี้วัด
                        <div style={{ fontSize: "11px" }}>
                          {moment(currentPeriod.setupStartDate).format("D MMM")}{" "}
                          -{" "}
                          {moment(currentPeriod.setupEndDate).format(
                            "D MMM BB"
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`col-md-3 col-6 mb-1 ${
                        stage === 2
                          ? "font-weight-bold text-warning"
                          : "text-muted"
                      }`}
                    >
                      <div
                        className={`p-2 rounded border ${
                          stage === 2 ? "bg-light border-warning shadow-sm" : ""
                        }`}
                      >
                        2. Defend & ยอมรับ
                        <div style={{ fontSize: "11px" }}>
                          {moment(currentPeriod.defendStartDate).format(
                            "D MMM"
                          )}{" "}
                          -{" "}
                          {moment(currentPeriod.defendEndDate).format(
                            "D MMM BB"
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`col-md-3 col-6 mb-1 ${
                        stage === 3
                          ? "font-weight-bold text-info"
                          : "text-muted"
                      }`}
                    >
                      <div
                        className={`p-2 rounded border ${
                          stage === 3 ? "bg-light border-info shadow-sm" : ""
                        }`}
                      >
                        3. ประเมินตนเอง
                        <div style={{ fontSize: "11px" }}>
                          {moment(currentPeriod.selfAssessStartDate).format(
                            "D MMM"
                          )}{" "}
                          -{" "}
                          {moment(currentPeriod.selfAssessEndDate).format(
                            "D MMM BB"
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`col-md-3 col-6 mb-1 ${
                        stage === 4
                          ? "font-weight-bold text-success"
                          : "text-muted"
                      }`}
                    >
                      <div
                        className={`p-2 rounded border ${
                          stage === 4 ? "bg-light border-success shadow-sm" : ""
                        }`}
                      >
                        4. สรุปคะแนน & กลั่นกรอง
                        <div style={{ fontSize: "11px" }}>
                          {moment(currentPeriod.reviewStartDate).format(
                            "D MMM"
                          )}{" "}
                          -{" "}
                          {moment(currentPeriod.reviewEndDate).format(
                            "D MMM BB"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary Dashboard Cards */}
              {summaryData && (
                <div className="row mb-3">
                  {/* Card 1: น้ำหนักรวม */}
                  <div className="col-md-4 mb-2">
                    <div
                      className={`card h-100 p-3 border ${
                        isDisplayedWeightValid
                          ? "border-success bg-white"
                          : "border-warning bg-light"
                      }`}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div
                            className="text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            ผลรวมน้ำหนักคะแนน (% Total Weight)
                          </div>
                          <h3
                            className={`font-weight-bold mb-0 ${
                              isDisplayedWeightValid
                                ? "text-success"
                                : "text-danger"
                            }`}
                          >
                            {displayedTotalWeight}%
                          </h3>
                        </div>
                        <div>
                          {isDisplayedWeightValid ? (
                            <span
                              className="badge badge-success p-2"
                              style={{ fontSize: "12px" }}
                            >
                              <i className="fas fa-check-circle mr-1"></i> ครบ
                              100%
                            </span>
                          ) : (
                            <span
                              className="badge badge-danger p-2"
                              style={{ fontSize: "12px" }}
                            >
                              <i className="fas fa-exclamation-triangle mr-1"></i>{" "}
                              ไม่ครบ 100%
                            </span>
                          )}
                        </div>
                      </div>
                      {!isDisplayedWeightValid && (
                        <div
                          className="text-danger mt-2"
                          style={{ fontSize: "12px" }}
                        >
                          * ผลรวมน้ำหนักต้องเท่ากับ 100% พอดี
                          กรุณาปรับน้ำหนักตัวชี้วัดให้ครบ
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card 2: สถิติตัวชี้วัด */}
                  <div className="col-md-4 mb-2">
                    <div className="card h-100 p-3 border border-info bg-white">
                      <div className="d-flex justify-content-between align-items-center">
                        <div
                          className="text-muted font-weight-bold"
                          style={{ fontSize: "13px" }}
                        >
                          จำนวนตัวชี้วัดทั้งหมดของท่าน
                        </div>
                        {totalKpiCount > 0 &&
                        approvedKpiCount === totalKpiCount ? (
                          <span
                            className="badge badge-success px-2 py-1"
                            style={{ fontSize: "11px" }}
                          >
                            <i className="fas fa-check-circle mr-1"></i>{" "}
                            อนุมัติครบแล้ว
                          </span>
                        ) : (
                          <span
                            className="badge badge-warning px-2 py-1 text-dark"
                            style={{
                              fontSize: "11px",
                              backgroundColor: "#fef08a"
                            }}
                          >
                            <i className="fas fa-hourglass-half mr-1"></i>{" "}
                            รออนุมัติ {pendingKpiCount} ข้อ
                          </span>
                        )}
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <div>
                          <div
                            className="text-muted"
                            style={{ fontSize: "11px" }}
                          >
                            ทั้งหมด
                          </div>
                          <h3 className="font-weight-bold text-primary mb-0">
                            {totalKpiCount}{" "}
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: "normal"
                              }}
                            >
                              ข้อ
                            </span>
                          </h3>
                        </div>

                        <div style={{ fontSize: "12px" }}>
                          <div>
                            <span className="text-success font-weight-bold">
                              <i className="fas fa-check-circle mr-1"></i>{" "}
                              อนุมัติแล้ว:
                            </span>{" "}
                            <strong
                              className="text-success"
                              style={{ fontSize: "14px" }}
                            >
                              {approvedKpiCount}
                            </strong>{" "}
                            ข้อ
                          </div>
                          <div className="mt-1">
                            <span className="text-danger font-weight-bold">
                              <i className="fas fa-clock mr-1"></i>{" "}
                              ยังไม่อนุมัติ:
                            </span>{" "}
                            <strong
                              className="text-danger"
                              style={{ fontSize: "14px" }}
                            >
                              {pendingKpiCount}
                            </strong>{" "}
                            ข้อ
                          </div>
                        </div>
                      </div>

                      <div
                        className="border-top mt-2 pt-1 d-flex justify-content-between text-muted"
                        style={{ fontSize: "11px" }}
                      >
                        <span>
                          ส่วนกลาง:{" "}
                          <strong>
                            {displayedAssignments.filter(
                              a => a.kpiItem?.kpiType === 1
                            ).length || 0}
                          </strong>{" "}
                          ข้อ
                        </span>
                        <span>
                          ระดับหน่วยงาน:{" "}
                          <strong>
                            {displayedAssignments.filter(
                              a => a.kpiItem?.kpiType === 2
                            ).length || 0}
                          </strong>{" "}
                          ข้อ
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: สรุปผลคะแนนการประเมินตนเอง */}
                  <div className="col-md-4 mb-2">
                    <div className="card h-100 p-3 border border-primary bg-white">
                      <div className="text-muted" style={{ fontSize: "13px" }}>
                        ผลการประเมินตนเอง (แบบ ปผ.)
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-1">
                        <div>
                          <h3 className="font-weight-bold text-info mb-0">
                            {summaryData.scorePercentage}%
                          </h3>
                          <div
                            className="text-muted"
                            style={{ fontSize: "11px" }}
                          >
                            ได้ {summaryData.totalActualScore} / เต็ม{" "}
                            {summaryData.totalMaxScore} คะแนน
                          </div>
                        </div>
                        <div>
                          <span
                            className="badge badge-pill badge-primary px-3 py-2"
                            style={{ fontSize: "12px" }}
                          >
                            {
                              displayedAssignments.filter(
                                a => a.evaluationStatus === 3
                              ).length
                            }{" "}
                            / {displayedAssignments.length} ประเมินแล้ว
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Table รายการ KPI ของฉัน */}
              {isLoading ? (
                <div className="text-center p-4">
                  <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
                  <p className="mt-2">กำลังโหลดรายการตัวชี้วัดของท่าน...</p>
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                    <div className="d-flex align-items-center flex-wrap">
                      <small className="text-muted mr-2">
                        <i className="fas fa-sort mr-1"></i>
                        เรียงตาม:{" "}
                        <strong className="text-dark">
                          {sortField === "custom" && "ลำดับกำหนดเอง (Custom Order)"}
                          {sortField === "type" && "ประเภทตัวชี้วัด"}
                          {sortField === "code" && "รหัสตัวชี้วัด"}
                          {sortField === "name" && "ชื่อตัวชี้วัด"}
                          {sortField === "target" && "เป้าหมาย"}
                          {sortField === "weight" && "% น้ำหนัก"}
                          {sortField === "itemStatus" && "สถานะตัวชี้วัด"}
                          {sortField === "assignmentStatus" && "สถานะการมอบหมาย"}
                        </strong>{" "}
                        <span className="text-secondary">
                          ({sortOrder === "asc" ? "น้อยไปมาก / ก-ฮ" : "มากไปน้อย / ฮ-ก"})
                        </span>
                      </small>
                      {sortField !== "custom" && (
                        <>
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-primary ml-1 py-0 px-2"
                            style={{ fontSize: "11px", height: "24px" }}
                            onClick={handleSaveCurrentOrderAsCustom}
                            disabled={isReordering}
                            title="บันทึกลำดับที่เรียงตามคอลัมน์นี้ให้เป็นลำดับหลัก"
                          >
                            <i className="fas fa-save mr-1"></i> บันทึกลำดับนี้เป็นลำดับหลัก
                          </button>
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-secondary ml-1 py-0 px-2"
                            style={{ fontSize: "11px", height: "24px" }}
                            onClick={() => {
                              setSortField("custom");
                              setSortOrder("asc");
                            }}
                            title="กลับไปใช้ลำดับหลักที่กำหนดไว้"
                          >
                            <i className="fas fa-undo mr-1"></i> คืนค่าลำดับหลัก
                          </button>
                        </>
                      )}
                    </div>
                    <div className="d-flex align-items-center">
                      {isReordering && (
                        <span className="badge badge-info px-2 py-1 mr-2">
                          <i className="fas fa-spinner fa-spin mr-1"></i> กำลังบันทึกลำดับ...
                        </span>
                      )}
                      <small className="text-muted d-none d-md-inline">
                        (คลิกหัวตารางเพื่อเรียง หรือกดปุ่ม ▲ ▼ ที่ช่องลำดับเพื่อย้ายตำแหน่ง)
                      </small>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-bordered table-hover mt-1">
                      <thead className="thead-light">
                        <tr
                          style={{
                            backgroundColor: "#f8f9fa",
                            textAlign: "center"
                          }}
                        >
                          <th
                            style={{ width: "65px", cursor: "pointer", userSelect: "none" }}
                            onClick={() => handleSort("custom")}
                            title="คลิกเพื่อเรียงลำดับกำหนดเอง"
                          >
                            # {getSortIcon("custom")}
                          </th>
                          <th
                            style={{ width: "110px", cursor: "pointer", userSelect: "none" }}
                            onClick={() => handleSort("type")}
                            title="คลิกเพื่อเรียงตามประเภท"
                          >
                            ประเภท {getSortIcon("type")}
                          </th>
                          <th
                            style={{ width: "95px", cursor: "pointer", userSelect: "none" }}
                            onClick={() => handleSort("code")}
                            title="คลิกเพื่อเรียงตามรหัสตัวชี้วัด"
                          >
                            รหัส {getSortIcon("code")}
                          </th>
                          <th
                            style={{ cursor: "pointer", userSelect: "none" }}
                            onClick={() => handleSort("name")}
                            title="คลิกเพื่อเรียงตามชื่อตัวชี้วัด"
                          >
                            ชื่อตัวชี้วัดผลการปฏิบัติงาน (KPI) {getSortIcon("name")}
                          </th>
                          <th
                            style={{ width: "90px", cursor: "pointer", userSelect: "none" }}
                            onClick={() => handleSort("target")}
                            title="คลิกเพื่อเรียงตามเป้าหมาย"
                          >
                            เป้าหมาย {getSortIcon("target")}
                          </th>
                          <th
                            style={{ width: "95px", cursor: "pointer", userSelect: "none" }}
                            onClick={() => handleSort("weight")}
                            title="คลิกเพื่อเรียงตาม % น้ำหนัก"
                          >
                            % น้ำหนัก {getSortIcon("weight")}
                          </th>
                          <th
                            style={{ width: "135px", cursor: "pointer", userSelect: "none" }}
                            onClick={() => handleSort("itemStatus")}
                            title="คลิกเพื่อเรียงตามสถานะตัวชี้วัด"
                          >
                            สถานะตัวชี้วัด (KpiItem) {getSortIcon("itemStatus")}
                          </th>
                          <th
                            style={{ width: "165px", cursor: "pointer", userSelect: "none" }}
                            onClick={() => handleSort("assignmentStatus")}
                            title="คลิกเพื่อเรียงตามสถานะการมอบหมาย"
                          >
                            สถานะผู้ที่ได้รับ Assign
                            <br />
                            <small style={{ fontSize: "10px", color: "#6c757d" }}>
                              (KpiUserAssignment)
                            </small>{" "}
                            {getSortIcon("assignmentStatus")}
                          </th>
                          {(isSelfAssessPeriod || isReviewPeriod) && (
                            <th style={{ width: "130px" }}>ผลประเมินตนเอง</th>
                          )}
                          <th style={{ width: "180px" }}>การดำเนินการ</th>
                        </tr>
                      </thead>
                    <tbody>
                      {!displayedAssignments ||
                      displayedAssignments.length === 0 ? (
                        <tr>
                          <td
                            colSpan={
                              isSelfAssessPeriod || isReviewPeriod ? 10 : 9
                            }
                            className="text-center py-4 text-muted"
                          >
                            <i className="fas fa-info-circle mr-1"></i>{" "}
                            ยังไม่มีตัวชี้วัดที่ท่านได้รับมอบหมายในรอบนี้
                            <div className="mt-2">
                              ท่านสามารถกดปุ่ม{" "}
                              <strong>
                                {'"+ เพิ่มตัวชี้วัดของฉัน (Individual KPI)"'}
                              </strong>{" "}
                              ด้านบนเพื่อสร้างตัวชี้วัดเฉพาะบุคคลของตนเองได้
                            </div>
                          </td>
                        </tr>
                      ) : (
                        displayedAssignments.map((assignment, index) => {
                          const item = assignment.kpiItem || {};
                          const isCommon = item.kpiType === 1;
                          const isIndividual = item.kpiType === 2;
                          const isAccepted = assignment.agreementStatus === 1;
                          const isDefended = assignment.agreementStatus === 2;
                          const isSubmitted = assignment.evaluationStatus === 3;

                          const isFullyApproved =
                            assignment.isFullyApproved ||
                            Boolean(assignment.missionApprovedBy) ||
                            assignment.assignmentStatus === 2;

                          const isCreator = Boolean(
                            (item.createdBy &&
                              currentUserName &&
                              item.createdBy.toLowerCase() ===
                                currentUserName.toLowerCase()) ||
                              item.canEdit
                          );
                          const canEditItemDetails =
                            (isCreator || isAdmin) &&
                            canEdit &&
                            (assignment.canEdit ?? true);
                          const isFixedWeight = item.weightType === 1;
                          const canEditPersonalWeight =
                            !isFixedWeight &&
                            canEdit &&
                            (assignment.canEdit ?? true);

                          const isAssignmentCommitted =
                            assignment.isCommitted ||
                            Boolean(assignment.committedDate) ||
                            assignment.assignmentStatus === 1 ||
                            assignment.assignmentStatus === 2;

                          let currentReqLevel = assignment.currentRequiredLevel;
                          let currentReqRole =
                            assignment.currentRequiredRoleName;

                          if (
                            !isAssignmentCommitted ||
                            assignment.assignmentStatus === 0
                          ) {
                            currentReqLevel = 0;
                            currentReqRole = "รอยืนยัน";
                          } else if (
                            !currentReqLevel &&
                            !isFullyApproved &&
                            assignment.assignmentStatus !== 3
                          ) {
                            if (assignment.groupWorkApprovedBy) {
                              currentReqLevel = 3;
                              currentReqRole = "รอหัวหน้าภารกิจอนุมัติ";
                            } else if (assignment.deptApprovedBy) {
                              currentReqLevel = 2;
                              currentReqRole = "รอหัวหน้ากลุ่มงานอนุมัติ";
                            } else {
                              currentReqLevel = 1;
                              currentReqRole = "รอหัวหน้างานอนุมัติ";
                            }
                          }

                          return (
                            <tr key={assignment.assignmentID}>
                              <td
                                className="text-center align-middle"
                                style={{ whiteSpace: "nowrap" }}
                              >
                                <div className="d-flex align-items-center justify-content-center">
                                  <div
                                    className="d-inline-flex flex-column mr-1"
                                    style={{ width: "16px" }}
                                  >
                                    <button
                                      type="button"
                                      className="btn btn-link p-0"
                                      title="เลื่อนขึ้น"
                                      disabled={index === 0 || isReordering}
                                      onClick={e => {
                                        e.stopPropagation();
                                        handleMoveOrder(index, -1);
                                      }}
                                      style={{
                                        lineHeight: 1,
                                        fontSize: "11px",
                                        color:
                                          index === 0 ? "#ccc" : "#007bff",
                                        cursor:
                                          index === 0 ? "default" : "pointer",
                                        textDecoration: "none"
                                      }}
                                    >
                                      <i className="fas fa-chevron-up"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-link p-0"
                                      title="เลื่อนลง"
                                      disabled={
                                        index ===
                                          displayedAssignments.length - 1 ||
                                        isReordering
                                      }
                                      onClick={e => {
                                        e.stopPropagation();
                                        handleMoveOrder(index, 1);
                                      }}
                                      style={{
                                        lineHeight: 1,
                                        fontSize: "11px",
                                        color:
                                          index ===
                                          displayedAssignments.length - 1
                                            ? "#ccc"
                                            : "#007bff",
                                        cursor:
                                          index ===
                                          displayedAssignments.length - 1
                                            ? "default"
                                            : "pointer",
                                        textDecoration: "none"
                                      }}
                                    >
                                      <i className="fas fa-chevron-down"></i>
                                    </button>
                                  </div>
                                  <span className="font-weight-bold">
                                    {index + 1}
                                  </span>
                                </div>
                              </td>
                              <td className="text-center align-middle">
                                {isCommon ? (
                                  <span
                                    className="badge badge-primary px-2 py-1"
                                    style={{ fontSize: "11px" }}
                                  >
                                    <i className="fas fa-users mr-1"></i>{" "}
                                    ตัวชี้วัดส่วนกลาง
                                  </span>
                                ) : (
                                  <span
                                    className="badge badge-warning px-2 py-1 text-dark"
                                    style={{ fontSize: "11px" }}
                                  >
                                    <i className="fas fa-building mr-1"></i>{" "}
                                    ตัวชี้วัดระดับหน่วยงาน
                                  </span>
                                )}
                              </td>
                              <td className="text-center align-middle font-weight-bold">
                                {item.code || "-"}
                              </td>
                              <td className="align-middle">
                                <strong>{item.kpiName}</strong>
                                {item.planProjectName && (
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: "12px" }}
                                  >
                                    โครงการ: {item.planProjectName}
                                  </div>
                                )}
                                {assignment.defendRemark && (
                                  <div
                                    className="text-warning font-italic mt-1 d-flex align-items-center flex-wrap"
                                    style={{ fontSize: "12px" }}
                                  >
                                    <span className="mr-2">
                                      <i className="fas fa-shield-alt mr-1 text-warning"></i>{" "}
                                      Defend: {parseDefendData(assignment.defendRemark)?.reason || assignment.defendRemark}
                                    </span>
                                    <button
                                      type="button"
                                      className="btn btn-xs btn-outline-warning py-0 px-1 font-weight-bold"
                                      style={{ fontSize: "10px", lineHeight: "1.2" }}
                                      title="คลิกดูตารางเปรียบเทียบ ก่อนแก้ vs ที่จะแก้"
                                      onClick={() => handleOpenDefendModal(assignment)}
                                    >
                                      <i className="fas fa-columns mr-1"></i>ดูเปรียบเทียบ
                                    </button>
                                  </div>
                                )}
                              </td>
                              <td className="text-center align-middle">
                                <span className="badge badge-info px-2 py-1">
                                  ระดับ {item.targetScoreLevel}
                                </span>
                              </td>
                              <td className="text-center align-middle">
                                <div className="d-flex align-items-center justify-content-center">
                                  <span
                                    className="font-weight-bold text-primary mr-1"
                                    style={{ fontSize: "14px" }}
                                  >
                                    {assignment.weight}%
                                  </span>
                                  {isFixedWeight ? (
                                    <span
                                      className="badge badge-light border text-danger ml-1 px-1 py-0"
                                      title="น้ำหนักบังคับ (ทุกคนใช้ค่าน้ำหนักเดียวกันตามที่กำหนด)"
                                      style={{ fontSize: "10px" }}
                                    >
                                      <i className="fas fa-lock mr-1"></i>บังคับ
                                    </span>
                                  ) : canEditPersonalWeight ? (
                                    <button
                                      type="button"
                                      className="btn btn-xs btn-outline-primary p-1 ml-1"
                                      style={{
                                        lineHeight: 1,
                                        padding: "2px 6px",
                                        fontSize: "11px",
                                        borderRadius: "4px"
                                      }}
                                      title="ปรับปรุงน้ำหนักตัวชี้วัดส่วนตัว"
                                      onClick={() =>
                                        handleOpenWeightModal(assignment)
                                      }
                                    >
                                      <i className="fas fa-pencil-alt"></i>
                                    </button>
                                  ) : null}
                                </div>
                              </td>

                              {/* ช่อง 1: สถานะตัวชี้วัด (KpiItem) */}
                              <td className="text-center align-middle">
                                {item.status === 2 ? (
                                  <div>
                                    <span
                                      className="badge badge-success px-2 py-1 font-weight-bold"
                                      style={{ fontSize: "11px" }}
                                    >
                                      <i className="fas fa-check-circle mr-1"></i>{" "}
                                      อนุมัติแล้ว
                                    </span>
                                    {(item.approvedFullName ||
                                      item.approvedBy) && (
                                      <div
                                        className="text-muted mt-1"
                                        style={{ fontSize: "10px" }}
                                        title={`อนุมัติเกณฑ์โดย: ${item.approvedFullName ||
                                          item.approvedBy}`}
                                      >
                                        โดย:{" "}
                                        {item.approvedFullName ||
                                          item.approvedBy}
                                      </div>
                                    )}
                                  </div>
                                ) : item.status === 3 ? (
                                  <div>
                                    <span
                                      className="badge badge-danger px-2 py-1 font-weight-bold"
                                      style={{ fontSize: "11px" }}
                                    >
                                      <i className="fas fa-times-circle mr-1"></i>{" "}
                                      ไม่อนุมัติ
                                    </span>
                                    {item.reviewRemark && (
                                      <div
                                        className="text-danger mt-1"
                                        style={{ fontSize: "10px" }}
                                      >
                                        {item.reviewRemark}
                                      </div>
                                    )}
                                  </div>
                                ) : item.status === 0 ? (
                                  <span
                                    className="badge badge-secondary px-2 py-1 font-weight-bold"
                                    style={{ fontSize: "11px" }}
                                  >
                                    ร่าง
                                  </span>
                                ) : (
                                  <div>
                                    <span
                                      className="badge badge-warning px-2 py-1 font-weight-bold"
                                      style={{
                                        fontSize: "11px",
                                        color: "#7c2d12",
                                        backgroundColor: "#fef08a"
                                      }}
                                    >
                                      <i className="fas fa-clock mr-1"></i>{" "}
                                      รออนุมัติ
                                    </span>
                                    <div
                                      className="text-muted mt-1"
                                      style={{ fontSize: "10px" }}
                                    >
                                      (รอ Admin อนุมัติเกณฑ์)
                                    </div>
                                  </div>
                                )}
                              </td>

                              {/* ช่อง 2: สถานะผู้ที่ได้รับ Assign (KpiUserAssignment) */}
                              <td className="text-center align-middle">
                                {assignment.assignmentStatus === 3 ? (
                                  <div>
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm px-2 py-1 shadow-sm font-weight-bold"
                                      style={{
                                        fontSize: "11px",
                                        borderRadius: "4px"
                                      }}
                                      onClick={() =>
                                        handleOpenClarifyModal(assignment)
                                      }
                                      title="คลิกเพื่อดูคำชี้แจงจากหัวหน้าและตอบกลับคำชี้แจง"
                                    >
                                      <i className="fas fa-exclamation-triangle mr-1"></i>{" "}
                                      ส่งกลับแก้ไข
                                    </button>
                                    {assignment.subordinateClarification && (
                                      <div
                                        className="text-info mt-1"
                                        style={{
                                          fontSize: "10px",
                                          cursor: "pointer"
                                        }}
                                        onClick={() =>
                                          handleOpenClarifyModal(assignment)
                                        }
                                        title="คลิกดูคำชี้แจงตอบกลับที่ส่งไปแล้ว"
                                      >
                                        <i className="fas fa-reply mr-1"></i>{" "}
                                        ตอบคำชี้แจงแล้ว (รออนุมัติ)
                                      </div>
                                    )}
                                  </div>
                                ) : isFullyApproved ? (
                                  <div>
                                    <span
                                      className="badge badge-success px-2 py-1 font-weight-bold"
                                      style={{ fontSize: "11px" }}
                                    >
                                      <i className="fas fa-check-circle mr-1"></i>{" "}
                                      อนุมัติแล้ว
                                    </span>
                                    {assignment.missionApprovedFullName ? (
                                      <div
                                        className="text-muted mt-1"
                                        style={{ fontSize: "10px" }}
                                        title={`อนุมัติโดยหัวหน้าภารกิจ เมื่อ ${formatThaiDate(
                                          assignment.missionApprovedDate
                                        )}`}
                                      >
                                        หน.ภารกิจ:{" "}
                                        {assignment.missionApprovedFullName}
                                      </div>
                                    ) : assignment.groupWorkApprovedFullName ? (
                                      <div
                                        className="text-muted mt-1"
                                        style={{ fontSize: "10px" }}
                                        title={`อนุมัติโดยหัวหน้ากลุ่มงาน เมื่อ ${formatThaiDate(
                                          assignment.groupWorkApprovedDate
                                        )}`}
                                      >
                                        หน.กลุ่มงาน:{" "}
                                        {assignment.groupWorkApprovedFullName}
                                      </div>
                                    ) : assignment.deptApprovedFullName ? (
                                      <div
                                        className="text-muted mt-1"
                                        style={{ fontSize: "10px" }}
                                        title={`อนุมัติโดยหัวหน้างาน เมื่อ ${formatThaiDate(
                                          assignment.deptApprovedDate
                                        )}`}
                                      >
                                        หน.งาน:{" "}
                                        {assignment.deptApprovedFullName}
                                      </div>
                                    ) : null}
                                    {assignment.subordinateClarification && (
                                      <div
                                        className="text-info mt-1"
                                        style={{
                                          fontSize: "10px",
                                          cursor: "pointer"
                                        }}
                                        onClick={() =>
                                          handleOpenClarifyModal(assignment)
                                        }
                                        title="คลิกดูคำชี้แจงตอบกลับ"
                                      >
                                        <i className="fas fa-reply mr-1"></i>{" "}
                                        ตอบคำชี้แจงแล้ว
                                      </div>
                                    )}
                                  </div>
                                ) : !isAssignmentCommitted ||
                                  assignment.assignmentStatus === 0 ? (
                                  <div>
                                    <span
                                      className="badge badge-warning px-2 py-1 font-weight-bold"
                                      style={{
                                        fontSize: "11px",
                                        color: "#9a3412",
                                        backgroundColor: "#fed7aa"
                                      }}
                                      title="ตัวชี้วัดยังไม่ได้กดยืนยันส่ง (รอยืนยันโดยผู้รับการประเมิน)"
                                    >
                                      <i className="fas fa-edit mr-1"></i>{" "}
                                      รอยืนยัน
                                    </span>
                                    <div
                                      className="text-muted mt-1"
                                      style={{ fontSize: "10px" }}
                                    >
                                      (รอยืนยันส่งตัวชี้วัด)
                                    </div>
                                  </div>
                                ) : currentReqLevel === 1 ? (
                                  <div>
                                    <span
                                      className="badge badge-warning px-2 py-1 font-weight-bold"
                                      style={{
                                        fontSize: "11px",
                                        color: "#7c2d12",
                                        backgroundColor: "#fef08a"
                                      }}
                                      title="กำลังรอการอนุมัติจากหัวหน้างาน"
                                    >
                                      <i className="fas fa-hourglass-start mr-1"></i>{" "}
                                      รอหัวหน้างานอนุมัติ
                                    </span>
                                    {assignment.deptBossFullName && (
                                      <div
                                        className="text-muted mt-1"
                                        style={{ fontSize: "10px" }}
                                      >
                                        (หน.งาน: {assignment.deptBossFullName})
                                      </div>
                                    )}
                                    {assignment.subordinateClarification && (
                                      <div
                                        className="text-info mt-1"
                                        style={{
                                          fontSize: "10px",
                                          cursor: "pointer"
                                        }}
                                        onClick={() =>
                                          handleOpenClarifyModal(assignment)
                                        }
                                        title="คลิกดูคำชี้แจงตอบกลับที่ส่งไปแล้ว"
                                      >
                                        <i className="fas fa-reply mr-1"></i>{" "}
                                        ตอบคำชี้แจงแล้ว (รออนุมัติ)
                                      </div>
                                    )}
                                    {isDefended && (
                                      <div className="mt-1">
                                        <span
                                          className="badge badge-warning px-1 py-0 text-dark"
                                          style={{ fontSize: "10px" }}
                                        >
                                          <i className="fas fa-shield-alt mr-1"></i>{" "}
                                          ขอ Defend แล้ว
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                ) : currentReqLevel === 2 ? (
                                  <div>
                                    <span
                                      className="badge badge-info px-2 py-1 font-weight-bold text-white"
                                      style={{
                                        fontSize: "11px",
                                        backgroundColor: "#0284c7"
                                      }}
                                      title="ผ่านหัวหน้างานแล้ว กำลังรอการอนุมัติจากหัวหน้ากลุ่มงาน"
                                    >
                                      <i className="fas fa-hourglass-half mr-1"></i>{" "}
                                      รอหัวหน้ากลุ่มงานอนุมัติ
                                    </span>
                                    {assignment.groupWorkBossFullName && (
                                      <div
                                        className="text-muted mt-1"
                                        style={{ fontSize: "10px" }}
                                      >
                                        (หน.กลุ่มงาน:{" "}
                                        {assignment.groupWorkBossFullName})
                                      </div>
                                    )}
                                    {assignment.deptApprovedFullName && (
                                      <div
                                        className="text-success mt-1"
                                        style={{ fontSize: "10px" }}
                                        title={`อนุมัติเมื่อ ${formatThaiDate(
                                          assignment.deptApprovedDate
                                        )}`}
                                      >
                                        <i className="fas fa-check-circle mr-1"></i>{" "}
                                        ผ่านหัวหน้างานแล้ว
                                      </div>
                                    )}
                                    {assignment.subordinateClarification && (
                                      <div
                                        className="text-info mt-1"
                                        style={{
                                          fontSize: "10px",
                                          cursor: "pointer"
                                        }}
                                        onClick={() =>
                                          handleOpenClarifyModal(assignment)
                                        }
                                        title="คลิกดูคำชี้แจงตอบกลับที่ส่งไปแล้ว"
                                      >
                                        <i className="fas fa-reply mr-1"></i>{" "}
                                        ตอบคำชี้แจงแล้ว (รออนุมัติ)
                                      </div>
                                    )}
                                    {isDefended && (
                                      <div className="mt-1">
                                        <span
                                          className="badge badge-warning px-1 py-0 text-dark"
                                          style={{ fontSize: "10px" }}
                                        >
                                          <i className="fas fa-shield-alt mr-1"></i>{" "}
                                          ขอ Defend แล้ว
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                ) : currentReqLevel === 3 ? (
                                  <div>
                                    <span
                                      className="badge badge-primary px-2 py-1 font-weight-bold text-white"
                                      style={{
                                        fontSize: "11px",
                                        backgroundColor: "#4f46e5"
                                      }}
                                      title="ผ่านหัวหน้ากลุ่มงานแล้ว กำลังรอการอนุมัติขั้นสุดท้ายจากหัวหน้าภารกิจ"
                                    >
                                      <i className="fas fa-hourglass-end mr-1"></i>{" "}
                                      รอหัวหน้าภารกิจอนุมัติ
                                    </span>
                                    {assignment.missionBossFullName && (
                                      <div
                                        className="text-muted mt-1"
                                        style={{ fontSize: "10px" }}
                                      >
                                        (หน.ภารกิจ:{" "}
                                        {assignment.missionBossFullName})
                                      </div>
                                    )}
                                    {assignment.groupWorkApprovedFullName ? (
                                      <div
                                        className="text-success mt-1"
                                        style={{ fontSize: "10px" }}
                                        title={`อนุมัติเมื่อ ${formatThaiDate(
                                          assignment.groupWorkApprovedDate
                                        )}`}
                                      >
                                        <i className="fas fa-check-circle mr-1"></i>{" "}
                                        ผ่านหัวหน้ากลุ่มงานแล้ว
                                      </div>
                                    ) : assignment.deptApprovedFullName ? (
                                      <div
                                        className="text-success mt-1"
                                        style={{ fontSize: "10px" }}
                                        title={`อนุมัติเมื่อ ${formatThaiDate(
                                          assignment.deptApprovedDate
                                        )}`}
                                      >
                                        <i className="fas fa-check-circle mr-1"></i>{" "}
                                        ผ่านหัวหน้างานแล้ว
                                      </div>
                                    ) : null}
                                    {assignment.subordinateClarification && (
                                      <div
                                        className="text-info mt-1"
                                        style={{
                                          fontSize: "10px",
                                          cursor: "pointer"
                                        }}
                                        onClick={() =>
                                          handleOpenClarifyModal(assignment)
                                        }
                                        title="คลิกดูคำชี้แจงตอบกลับที่ส่งไปแล้ว"
                                      >
                                        <i className="fas fa-reply mr-1"></i>{" "}
                                        ตอบคำชี้แจงแล้ว (รออนุมัติ)
                                      </div>
                                    )}
                                    {isDefended && (
                                      <div className="mt-1">
                                        <span
                                          className="badge badge-warning px-1 py-0 text-dark"
                                          style={{ fontSize: "10px" }}
                                        >
                                          <i className="fas fa-shield-alt mr-1"></i>{" "}
                                          ขอ Defend แล้ว
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div>
                                    <span
                                      className="badge badge-secondary px-2 py-1 font-weight-bold"
                                      style={{ fontSize: "11px" }}
                                    >
                                      <i className="fas fa-clock mr-1"></i>{" "}
                                      {currentReqRole || "รออนุมัติ"}
                                    </span>
                                    {assignment.subordinateClarification && (
                                      <div
                                        className="text-info mt-1"
                                        style={{
                                          fontSize: "10px",
                                          cursor: "pointer"
                                        }}
                                        onClick={() =>
                                          handleOpenClarifyModal(assignment)
                                        }
                                        title="คลิกดูคำชี้แจงตอบกลับที่ส่งไปแล้ว"
                                      >
                                        <i className="fas fa-reply mr-1"></i>{" "}
                                        ตอบคำชี้แจงแล้ว
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>

                              {/* ผลประเมินตนเอง (Timeline 3 / 4) */}
                              {(isSelfAssessPeriod || isReviewPeriod) && (
                                <td className="text-center align-middle">
                                  {isSubmitted ? (
                                    <div>
                                      <span className="badge badge-success px-2 py-1">
                                        ระดับ {assignment.selfScoreLevel} (
                                        {assignment.selfActualScore} คะแนน)
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="badge badge-light border text-muted px-2 py-1">
                                      ยังไม่ประเมิน
                                    </span>
                                  )}
                                </td>
                              )}

                              {/* การดำเนินการ (Actions) */}
                              <td className="text-center align-middle">
                                {/* ปุ่มดูเกณฑ์ 5 ระดับ */}
                                <button
                                  className="btn btn-sm btn-outline-secondary mr-1"
                                  title="ดูเกณฑ์ 5 ระดับและสูตรคำนวณ"
                                  onClick={() =>
                                    handleOpenDetailModal(assignment)
                                  }
                                >
                                  <i className="fas fa-eye"></i>
                                </button>

                                {/* KpiType = 1 (Common): อนุมัติทันที ไม่มีปุ่ม Defend หรือ ยอมรับ */}

                                {/* KpiType = 2 (Individual): 
                                    - Stage 1 (ช่วงจัดทำ/ปรับแก้): ขอ Defend ได้ และสามารถคลิกดู/แก้ไขเพิ่มเติมได้
                                    - Stage >= 2 (ช่วง Defend ตัวชี้วัด / กรรมการพิจารณา): สิ้นสุดช่วงเวลาขอแก้ Defend แล้ว ไม่สามารถขอใหม่ได้ */}
                                {isIndividual &&
                                  stage === 1 &&
                                  (isFullyApproved ||
                                    isAssignmentCommitted ||
                                    !canEdit) && (
                                    <button
                                      className={`btn btn-sm mr-1 font-weight-bold ${
                                        isDefended
                                          ? "btn-warning text-dark"
                                          : "btn-outline-warning text-dark"
                                      }`}
                                      title={
                                        isDefended
                                          ? "ขอ Defend แล้ว (คลิกเพื่อดูการเปรียบเทียบก่อน-หลัง หรือแก้ไขเพิ่มเติม)"
                                          : "ขอ Defend ปรับเกณฑ์ตัวชี้วัด (แจ้งรายละเอียดเพื่อให้กรรมการพิจารณา)"
                                      }
                                      onClick={() =>
                                        handleOpenDefendModal(assignment)
                                      }
                                    >
                                      <i className="fas fa-shield-alt mr-1"></i>
                                      {isDefended
                                        ? "ขอ Defend แล้ว (ดู/แก้ไข)"
                                        : "ขอ Defend"}
                                    </button>
                                  )}

                                {/* เมื่อเข้าสู่ Stage >= 2 (ช่วง Defend หรือถัดไป):
                                    - หากเคยขอ Defend ไว้ จะสามารถคลิกดูตารางเปรียบเทียบได้อย่างเดียว (แก้เพิ่มไม่ได้)
                                    - หากยังไม่ได้ขอ Defend จะไม่มีปุ่มขอ Defend แสดงอีก */}
                                {isIndividual && isDefended && stage >= 2 && (
                                  <button
                                    className="btn btn-sm btn-outline-warning text-dark mr-1 font-weight-bold"
                                    style={{ fontSize: "11px" }}
                                    title="ดูรายละเอียดการขอ Defend (เปรียบเทียบก่อน-หลัง - สิ้นสุดช่วงเวลาขอแก้ไขแล้ว)"
                                    onClick={() =>
                                      handleOpenDefendModal(assignment)
                                    }
                                  >
                                    <i className="fas fa-shield-alt mr-1"></i>
                                    ขอ Defend แล้ว (ดูข้อมูล)
                                  </button>
                                )}

                                {/* ปรับปรุงน้ำหนักตัวชี้วัดส่วนตัว (ทุกตัวชี้วัดที่ได้รับมอบหมาย ไม่ว่าจะเป็น Common หรือ Individual) */}
                                {canEditPersonalWeight && (
                                  <button
                                    className="btn btn-sm btn-outline-primary mr-1"
                                    title="ปรับปรุงน้ำหนักตัวชี้วัดส่วนตัว"
                                    onClick={() =>
                                      handleOpenWeightModal(assignment)
                                    }
                                  >
                                    <i className="fas fa-weight-hanging"></i>
                                  </button>
                                )}

                                {/* แก้ไขรายละเอียดตัวชี้วัด (เฉพาะคนที่สร้าง หรือ Admin) */}
                                {isIndividual &&
                                  canManageIndividual &&
                                  canEditItemDetails && (
                                    <button
                                      className="btn btn-sm btn-outline-info mr-1"
                                      title="แก้ไขรายละเอียดตัวชี้วัด"
                                      onClick={() =>
                                        handleOpenEditIndividualModal(
                                          assignment
                                        )
                                      }
                                    >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                  )}

                                {/* ลบตัวชี้วัดส่วนบุคคล (เฉพาะคนที่สร้าง หรือ Admin) */}
                                {isIndividual &&
                                  canManageIndividual &&
                                  canEditItemDetails && (
                                    <button
                                      className="btn btn-sm btn-outline-danger mr-1"
                                      title="ลบตัวชี้วัดส่วนบุคคล"
                                      onClick={() =>
                                        handleDeleteIndividualKpi(assignment)
                                      }
                                    >
                                      <i className="fas fa-trash-alt"></i>
                                    </button>
                                  )}

                                {/* ล็อกการแก้ไขเมื่อ Committed */}
                                {!canEdit && !isSelfAssessPeriod && (
                                  <span
                                    className="badge badge-light border text-muted px-2 py-1 mr-1"
                                    title="ล็อกการแก้ไขเนื่องจากส่งตัวชี้วัดแล้ว"
                                    style={{ fontSize: "11px" }}
                                  >
                                    <i className="fas fa-lock mr-1"></i> ล็อก
                                  </span>
                                )}

                                {/* ปุ่มประเมินตนเอง (ในช่วง Self-Assessment) */}
                                {isSelfAssessPeriod && (
                                  <button
                                    className={`btn btn-sm ${
                                      isSubmitted
                                        ? "btn-outline-primary"
                                        : "btn-primary"
                                    }`}
                                    title={
                                      isSubmitted
                                        ? "แก้ไขผลประเมินตนเอง"
                                        : "บันทึกผลการประเมินตนเอง"
                                    }
                                    onClick={() =>
                                      handleOpenSelfAssessModal(assignment)
                                    }
                                  >
                                    <i className="fas fa-pencil-alt mr-1"></i>
                                    {isSubmitted ? "แก้ไข" : "ประเมิน"}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {displayedAssignments &&
                      displayedAssignments.length > 0 && (
                        <tfoot>
                          <tr
                            style={{
                              backgroundColor: "#f8f9fa",
                              fontWeight: "bold"
                            }}
                          >
                            <td colSpan="5" className="text-right align-middle">
                              รวมน้ำหนักทั้งหมด:
                            </td>
                            <td
                              className={`text-center align-middle font-weight-bold ${
                                isDisplayedWeightValid
                                  ? "text-success"
                                  : "text-danger"
                              }`}
                              style={{ fontSize: "14px" }}
                            >
                              {displayedTotalWeight}%
                            </td>
                            <td
                              colSpan={
                                isSelfAssessPeriod || isReviewPeriod ? 4 : 3
                              }
                              className="align-middle"
                            >
                              {!isDisplayedWeightValid && (
                                <span className="text-danger small font-weight-bold">
                                  <i className="fas fa-exclamation-triangle mr-1"></i>{" "}
                                  (น้ำหนักรวมต้องเท่ากับ 100%)
                                </span>
                              )}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                  </table>
                </div>
              </>
            )}
            </CardBody>
          </Card>
        </Col>
      </Grid>

      {/* ========================================================================= */}
      {/* 1. Modal สร้าง / แก้ไขตัวชี้วัดส่วนบุคคล (Individual KPI Modal) */}
      {/* ========================================================================= */}
      <Modal
        isOpen={isIndividualModalOpen}
        onRequestClose={() => setIsIndividualModalOpen(false)}
        style={{
          overlay: styles.modalOverlay,
          content: styles.modalContent
        }}
        contentLabel="สร้าง/แก้ไขตัวชี้วัดส่วนบุคคล"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "2px solid #28a745",
            paddingBottom: "10px"
          }}
        >
          <h4 style={{ margin: 0, color: "#28a745", fontWeight: "bold" }}>
            <i className="fas fa-user-edit mr-2"></i>
            {editingItemId
              ? "แก้ไขตัวชี้วัดส่วนบุคคล (Edit Individual KPI)"
              : "สร้างตัวชี้วัดของฉัน (New Individual KPI)"}
          </h4>
          <button
            type="button"
            className="close"
            onClick={() => setIsIndividualModalOpen(false)}
          >
            <span>&times;</span>
          </button>
        </div>

        <form onSubmit={handleSaveIndividualKpi} className="mt-3">
          <div className="form-row">
            <div className="form-group col-md-6">
              <label className="font-weight-bold">รหัสตัวชี้วัด (Code)</label>
              <input
                type="text"
                className="form-control"
                value={individualFormData.Code}
                onChange={e =>
                  setIndividualFormData({
                    ...individualFormData,
                    Code: e.target.value
                  })
                }
                placeholder="เช่น MY-KPI-1"
              />
            </div>

            <div className="form-group col-md-6">
              <label className="font-weight-bold">หน่วยวัด (Unit)</label>
              <input
                type="text"
                className="form-control"
                value={individualFormData.UnitName}
                onChange={e =>
                  setIndividualFormData({
                    ...individualFormData,
                    UnitName: e.target.value
                  })
                }
                placeholder="เช่น ร้อยละ, ชิ้นงาน, ครั้ง"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="font-weight-bold">
              ชื่อตัวชี้วัดผลการปฏิบัติงาน (KPI Name){" "}
              <span className="text-danger">*</span>
            </label>
            <textarea
              rows={2}
              className="form-control"
              value={individualFormData.KpiName}
              onChange={e =>
                setIndividualFormData({
                  ...individualFormData,
                  KpiName: e.target.value
                })
              }
              placeholder="ระบุชื่อตัวชี้วัดผลงานของท่าน"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group col-md-6">
              <label className="font-weight-bold">
                ระดับค่าเป้าหมาย <span className="text-danger">*</span>
              </label>
              <select
                className="form-control font-weight-bold text-primary"
                value={individualFormData.TargetScoreLevel}
                onChange={e =>
                  setIndividualFormData({
                    ...individualFormData,
                    TargetScoreLevel: parseInt(e.target.value, 10)
                  })
                }
                required
              >
                <option value={1}>ระดับ 1</option>
                <option value={2}>ระดับ 2</option>
                <option value={3}>ระดับ 3 (เป้าหมายมาตรฐาน)</option>
                <option value={4}>ระดับ 4</option>
                <option value={5}>ระดับ 5 (ดีเยี่ยม)</option>
              </select>
            </div>

            <div className="form-group col-md-6">
              <label className="font-weight-bold">
                ค่าน้ำหนัก (% Weight) <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  className="form-control font-weight-bold text-success"
                  value={individualFormData.DefaultWeight}
                  onChange={e =>
                    setIndividualFormData({
                      ...individualFormData,
                      DefaultWeight: parseFloat(e.target.value) || 0
                    })
                  }
                  required
                />
                <div className="input-group-append">
                  <span className="input-group-text">%</span>
                </div>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="font-weight-bold">
              คำอธิบายตัวชี้วัด (Description)
            </label>
            <textarea
              rows={2}
              className="form-control"
              value={individualFormData.Description}
              onChange={e =>
                setIndividualFormData({
                  ...individualFormData,
                  Description: e.target.value
                })
              }
              placeholder="คำอธิบายรายละเอียดตัวชี้วัด"
            />
          </div>

          {/* เกณฑ์ 5 ระดับ */}
          <h6 className="font-weight-bold text-primary border-bottom pb-2 mt-3">
            <i className="fas fa-list-ol mr-2"></i> เกณฑ์การให้คะแนน 5 ระดับ
            (Evaluation Criteria)
          </h6>

          <div className="form-group">
            <label className="font-weight-bold text-danger">ระดับ 1:</label>
            <textarea
              rows={2}
              className="form-control"
              value={individualFormData.CriteriaLevel1}
              onChange={e =>
                setIndividualFormData({
                  ...individualFormData,
                  CriteriaLevel1: e.target.value
                })
              }
            />
          </div>

          <div className="form-group">
            <label className="font-weight-bold text-warning">ระดับ 2:</label>
            <textarea
              rows={2}
              className="form-control"
              value={individualFormData.CriteriaLevel2}
              onChange={e =>
                setIndividualFormData({
                  ...individualFormData,
                  CriteriaLevel2: e.target.value
                })
              }
            />
          </div>

          <div className="form-group">
            <label className="font-weight-bold text-primary">
              ระดับ 3 (ค่าเป้าหมาย): <span className="text-danger">*</span>
            </label>
            <textarea
              rows={2}
              className="form-control"
              value={individualFormData.CriteriaLevel3}
              onChange={e =>
                setIndividualFormData({
                  ...individualFormData,
                  CriteriaLevel3: e.target.value
                })
              }
              required
            />
          </div>

          <div className="form-group">
            <label className="font-weight-bold text-info">ระดับ 4:</label>
            <textarea
              rows={2}
              className="form-control"
              value={individualFormData.CriteriaLevel4}
              onChange={e =>
                setIndividualFormData({
                  ...individualFormData,
                  CriteriaLevel4: e.target.value
                })
              }
            />
          </div>

          <div className="form-group">
            <label className="font-weight-bold text-success">
              ระดับ 5 (ดีเยี่ยม):
            </label>
            <textarea
              rows={2}
              className="form-control"
              value={individualFormData.CriteriaLevel5}
              onChange={e =>
                setIndividualFormData({
                  ...individualFormData,
                  CriteriaLevel5: e.target.value
                })
              }
            />
          </div>

          <div className="form-group">
            <label className="font-weight-bold">
              สูตรคำนวณ (Calculation Formula)
            </label>
            <input
              type="text"
              className="form-control"
              value={individualFormData.CalculationFormula}
              onChange={e =>
                setIndividualFormData({
                  ...individualFormData,
                  CalculationFormula: e.target.value
                })
              }
              placeholder="เช่น (ผลงานจริง / เป้าหมาย) x 100"
            />
          </div>

          <div className="text-right border-top pt-3 mt-3">
            <Button
              color="transparent"
              onClick={() => setIsIndividualModalOpen(false)}
              disabled={isSaving}
            >
              ยกเลิก
            </Button>
            <Button color="success" type="submit" disabled={isSaving}>
              {isSaving ? (
                <i className="fas fa-spinner fa-spin mr-1"></i>
              ) : (
                <i className="fas fa-save mr-1"></i>
              )}
              {editingItemId ? "บันทึกการแก้ไข" : "สร้างตัวชี้วัดของฉัน"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ========================================================================= */}
      {/* 2. Modal ขอ Defend ตัวชี้วัดส่วนกลาง (Defend Common KPI Modal) */}
      {/* ========================================================================= */}
      <Modal
        isOpen={isDefendModalOpen}
        onRequestClose={() => setIsDefendModalOpen(false)}
        style={{
          overlay: styles.modalOverlay,
          content: styles.modalContent
        }}
        contentLabel="ขอ Defend ปรับเกณฑ์ตัวชี้วัด"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "2px solid #ffc107",
            paddingBottom: "10px"
          }}
        >
          <h4 style={{ margin: 0, color: "#d39e00", fontWeight: "bold" }}>
            <i className="fas fa-shield-alt mr-2"></i>{" "}
            {stage >= 2
              ? "ดูข้อมูลการขอ Defend ปรับเกณฑ์ตัวชี้วัด (เปรียบเทียบก่อน-หลัง)"
              : "ขอ Defend ปรับเกณฑ์ตัวชี้วัด (แจ้งรายละเอียดเพื่อให้กรรมการพิจารณา)"}
          </h4>
          <button
            type="button"
            className="close"
            onClick={() => setIsDefendModalOpen(false)}
          >
            <span>&times;</span>
          </button>
        </div>

        {stage >= 2 ? (
          <div className="alert alert-info mt-3 mb-3 py-2" style={{ fontSize: "13px" }}>
            <i className="fas fa-info-circle mr-1"></i>{" "}
            <strong>คำชี้แจง:</strong> ปัจจุบันอยู่ในช่วง Defend ตัวชี้วัด (สำหรับคณะกรรมการพิจารณาและอนุมัติ) ระบบเปิดให้<strong>ดูตารางเปรียบเทียบได้อย่างเดียว ไม่สามารถขอแก้ไขข้อมูลเพิ่มเติมได้</strong>
          </div>
        ) : (
          <div className="alert alert-warning mt-3 mb-3 py-2" style={{ fontSize: "13px" }}>
            <i className="fas fa-info-circle mr-1"></i> <strong>คำชี้แจง:</strong> ข้อมูลก่อนแก้เป็น Text ReadOnly (ข้อมูลจริงในฐานข้อมูล) ท่านสามารถพิมพ์ปรับปรุงข้อมูลในช่อง <strong>"ข้อมูลที่จะแก้"</strong> ในตารางด้านล่างได้โดยตรง หากมีการแก้ไขข้อมูล ระบบจะเปลี่ยนสถานะเป็น <strong>"ขอแก้ไข"</strong> ทันที เมื่อบันทึกระบบจะเก็บข้อเสนอไว้ในตารางคำขอ Defend เพื่อเสนอให้คณะกรรมการพิจารณาและอนุมัติในขั้นตอนต่อไป
          </div>
        )}

        <form onSubmit={handleSubmitDefend}>
          {(() => {
            const compRows = getComparisonRows();
            const diffCount = compRows.filter(
              r =>
                !r.isHeader &&
                String(r.origRaw ?? "").trim() !==
                  String(r.propRaw ?? "").trim()
            ).length;
            const isReadOnlyDefend = stage >= 2;

            return (
              <div>
                <div className="card border-warning mb-3 shadow-sm">
                  <div
                    className="card-header bg-warning py-1 px-3 text-dark font-weight-bold d-flex justify-content-between align-items-center"
                    style={{ fontSize: "13px" }}
                  >
                    <span>
                      <i className="fas fa-comment-dots mr-1"></i> เหตุผลความจำเป็นในการขอ Defend{" "}
                      <span className="text-danger">*</span>
                    </span>
                    {diffCount > 0 ? (
                      <span
                        className="badge badge-danger px-2 py-1"
                        style={{ fontSize: "11px" }}
                      >
                        <i className="fas fa-exchange-alt mr-1"></i> ขอแก้ไข {diffCount} รายการ
                      </span>
                    ) : (
                      <span
                        className="badge badge-secondary px-2 py-1"
                        style={{ fontSize: "11px" }}
                      >
                        ข้อมูลตรงกับเกณฑ์เดิม (ยังไม่มีการขอแก้ไข)
                      </span>
                    )}
                  </div>
                  <div className="card-body py-2 px-3 bg-light">
                    {isReadOnlyDefend ? (
                      <div
                        className="text-dark font-weight-bold"
                        style={{ fontSize: "13px", whiteSpace: "pre-wrap" }}
                      >
                        {defendFormData.Reason || (
                          <span className="text-muted font-italic">
                            - ไม่ได้ระบุเหตุผล -
                          </span>
                        )}
                      </div>
                    ) : (
                      <textarea
                        rows={2}
                        className="form-control"
                        style={{ fontSize: "13px" }}
                        placeholder="กรุณาระบุเหตุผลหรือความจำเป็นในการขอปรับเกณฑ์ตัวชี้วัดนี้ เช่น เนื่องจากบริบทภาระงานของหน่วยงาน หรือข้อจำกัดด้านทรัพยากร..."
                        value={defendFormData.Reason}
                        required
                        onChange={e =>
                          setDefendFormData({
                            ...defendFormData,
                            Reason: e.target.value
                          })
                        }
                      />
                    )}
                  </div>
                </div>

                <div
                  className="table-responsive border rounded"
                  style={{ maxHeight: "480px", overflowY: "auto" }}
                >
                  <table
                    className="table table-sm table-bordered mb-0"
                    style={{ fontSize: "12px" }}
                  >
                    <thead className="thead-light">
                      <tr style={{ textAlign: "center", backgroundColor: "#f1f5f9" }}>
                        <th style={{ width: "20%", verticalAlign: "middle" }}>
                          หัวข้อ / รายการ
                        </th>
                        <th
                          style={{
                            width: "36%",
                            backgroundColor: "#f8f9fa",
                            verticalAlign: "middle"
                          }}
                        >
                          <i className="fas fa-lock mr-1 text-secondary"></i> ข้อมูลก่อนแก้ (Text ReadOnly)
                        </th>
                        <th
                          style={{
                            width: "36%",
                            backgroundColor: "#fff8e1",
                            verticalAlign: "middle"
                          }}
                        >
                          <i className="fas fa-edit mr-1 text-warning"></i> ข้อมูลที่จะแก้ (แก้ไขได้)
                        </th>
                        <th style={{ width: "8%", verticalAlign: "middle" }}>
                          สถานะ
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {compRows.map((row, idx) => {
                        if (row.isHeader) {
                          return (
                            <tr
                              key={row.id || idx}
                              style={{ backgroundColor: row.bg || "#f1f5f9" }}
                            >
                              <td
                                colSpan={4}
                                className="font-weight-bold py-2 px-3"
                                style={{
                                  fontSize: "13px",
                                  color: row.color || "#0369a1",
                                  borderTop: "2px solid #cbd5e1",
                                  borderBottom: "1px solid #cbd5e1"
                                }}
                              >
                                <i
                                  className={`fas ${
                                    row.icon || "fa-layer-group"
                                  } mr-2`}
                                ></i>
                                {row.headerTitle}
                              </td>
                            </tr>
                          );
                        }
                        const isDiff =
                          String(row.origRaw ?? "").trim() !==
                          String(row.propRaw ?? "").trim();
                        return (
                          <tr
                            key={row.id || idx}
                            style={isDiff ? { backgroundColor: "#fffbf0" } : {}}
                          >
                            <td className="align-middle font-weight-bold text-dark">
                              {row.field}
                            </td>
                            <td
                              className="align-middle bg-light text-muted p-2"
                              style={{
                                whiteSpace: "pre-wrap",
                                fontSize: "12px",
                                borderRight: "1px solid #dee2e6"
                              }}
                            >
                              {row.origDisplay}
                            </td>
                            <td
                              className="align-middle p-2"
                              style={{
                                backgroundColor: isDiff ? "#fffde7" : "#ffffff",
                                transition: "background-color 0.2s"
                              }}
                            >
                              {row.renderInput(isReadOnlyDefend)}
                            </td>
                            <td className="text-center align-middle p-2">
                              {isDiff ? (
                                <span className="badge badge-warning text-dark font-weight-bold px-2 py-1 shadow-sm">
                                  <i className="fas fa-edit mr-1"></i>ขอแก้ไข
                                </span>
                              ) : (
                                <span className="badge badge-light border text-muted px-2 py-1">
                                  คงเดิม
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex justify-content-between align-items-center border-top pt-3 mt-3">
                  <div>
                    <span className="text-muted" style={{ fontSize: "13px" }}>
                      <i className="fas fa-info-circle mr-1"></i>
                      {diffCount > 0 ? (
                        <span className="text-warning font-weight-bold">
                          มีการขอแก้ไข {diffCount} รายการ (ระบบจะเก็บเข้าตารางขอแก้ Defend)
                        </span>
                      ) : (
                        <span>ข้อมูลตรงกับเกณฑ์เดิมในฐานข้อมูล (คงเดิมทั้งหมด)</span>
                      )}
                    </span>
                  </div>
                  <div>
                    <Button
                      color="transparent"
                      onClick={() => setIsDefendModalOpen(false)}
                      disabled={isSaving}
                      className="mr-2"
                    >
                      {isReadOnlyDefend ? "ปิดหน้าต่าง" : "ยกเลิก"}
                    </Button>
                    {!isReadOnlyDefend && (
                      <Button color="warning" type="submit" disabled={isSaving}>
                        {isSaving ? (
                          <i className="fas fa-spinner fa-spin mr-1"></i>
                        ) : (
                          <i className="fas fa-paper-plane mr-1"></i>
                        )}
                        บันทึกและส่งคำขอ Defend
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </form>
      </Modal>

      {/* ========================================================================= */}
      {/* 3. Modal ดูรายละเอียดตัวชี้วัด (Detail Modal) */}
      {/* ========================================================================= */}
      <Modal
        isOpen={isDetailModalOpen}
        onRequestClose={() => setIsDetailModalOpen(false)}
        style={{
          overlay: styles.modalOverlay,
          content: styles.modalContent
        }}
        contentLabel="รายละเอียดตัวชี้วัด"
      >
        {activeItem && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "2px solid #17a2b8",
                paddingBottom: "10px"
              }}
            >
              <h4 style={{ margin: 0, color: "#17a2b8", fontWeight: "bold" }}>
                <i className="fas fa-info-circle mr-2"></i> รายละเอียดตัวชี้วัด:{" "}
                {activeItem.code}
              </h4>
              <button
                type="button"
                className="close"
                onClick={() => setIsDetailModalOpen(false)}
              >
                <span>&times;</span>
              </button>
            </div>

            <div className="mt-3">
              <h5 className="font-weight-bold text-dark">
                {activeItem.kpiName}
              </h5>
              <div className="mb-2">
                <span className="badge badge-primary mr-2">
                  {activeItem.kpiType === 1
                    ? "ตัวชี้วัดส่วนกลาง (Central KPI)"
                    : "ตัวชี้วัดระดับหน่วยงาน (Department KPI)"}
                </span>
                <span className="badge badge-success mr-2">
                  น้ำหนัก:{" "}
                  {activeAssignment?.weight || activeItem.defaultWeight}%
                </span>
                <span className="badge badge-info mr-2">
                  เป้าหมาย: ระดับ {activeItem.targetScoreLevel}
                </span>
                <span className="badge badge-secondary">
                  หน่วย: {activeItem.unitName || "ร้อยละ"}
                </span>
              </div>

              {activeItem.description && (
                <p className="text-muted" style={{ fontSize: "14px" }}>
                  {activeItem.description}
                </p>
              )}

              {/* ลำดับขั้นการอนุมัติ (Approval Status Stepper) */}
              {activeAssignment && (
                <div className="card mb-3 border bg-light p-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong style={{ fontSize: "14px" }}>
                      <i className="fas fa-tasks text-primary mr-1"></i>{" "}
                      สถานะการอนุมัติตามลำดับขั้น (Approval Progress):
                    </strong>
                    {activeAssignment.isFullyApproved ||
                    activeAssignment.missionApprovedBy ||
                    activeAssignment.assignmentStatus === 2 ? (
                      <span className="badge badge-success px-2 py-1 font-weight-bold">
                        <i className="fas fa-check-circle mr-1"></i> อนุมัติแล้ว
                        (เสร็จสิ้น)
                      </span>
                    ) : activeAssignment.assignmentStatus === 3 ? (
                      <span className="badge badge-danger px-2 py-1 font-weight-bold">
                        <i className="fas fa-exclamation-triangle mr-1"></i>{" "}
                        ส่งกลับแก้ไข
                      </span>
                    ) : !activeAssignment.isCommitted &&
                      !activeAssignment.committedDate &&
                      activeAssignment.assignmentStatus === 0 ? (
                      <span
                        className="badge badge-warning px-2 py-1 font-weight-bold"
                        style={{ color: "#9a3412", backgroundColor: "#fed7aa" }}
                      >
                        <i className="fas fa-edit mr-1"></i> รอยืนยัน
                      </span>
                    ) : (
                      <span
                        className="badge badge-warning px-2 py-1 font-weight-bold"
                        style={{ color: "#7c2d12", backgroundColor: "#fef08a" }}
                      >
                        <i className="fas fa-hourglass-half mr-1"></i>{" "}
                        {activeAssignment.currentRequiredRoleName ||
                          "รออนุมัติ"}
                      </span>
                    )}
                  </div>

                  <div
                    className="d-flex flex-wrap align-items-center mt-1"
                    style={{ fontSize: "12px" }}
                  >
                    {/* Step 1: Dept */}
                    <div className="mr-1 mb-1">
                      {!activeAssignment.hasDeptBoss ? (
                        <span
                          className="badge badge-secondary py-1 px-2"
                          title="ไม่มีหัวหน้างาน (ข้ามระดับนี้อัตโนมัติ)"
                        >
                          1. หน่วยงาน: -
                        </span>
                      ) : activeAssignment.deptApprovedBy ? (
                        <span
                          className="badge badge-success py-1 px-2"
                          title={`อนุมัติโดย ${activeAssignment.deptApprovedFullName ||
                            activeAssignment.deptApprovedBy} เมื่อ ${formatThaiDate(
                            activeAssignment.deptApprovedDate
                          )}`}
                        >
                          <i className="fas fa-check mr-1"></i>
                          1. หน.งาน:{" "}
                          {activeAssignment.deptApprovedFullName ||
                            activeAssignment.deptApprovedBy}
                        </span>
                      ) : activeAssignment.currentRequiredLevel === 1 ? (
                        <span
                          className="badge badge-warning py-1 px-2 font-weight-bold"
                          style={{
                            color: "#7c2d12",
                            backgroundColor: "#fef08a"
                          }}
                        >
                          <i className="fas fa-hourglass-start mr-1"></i>
                          1. รอ หน.งาน (
                          {activeAssignment.deptBossFullName || "รออนุมัติ"})
                        </span>
                      ) : (
                        <span className="badge badge-light border text-muted py-1 px-2">
                          1. รอ หน.งาน
                        </span>
                      )}
                    </div>

                    <i
                      className="fas fa-chevron-right text-muted mx-1"
                      style={{ fontSize: "10px" }}
                    ></i>

                    {/* Step 2: GroupWork */}
                    <div className="mr-1 mb-1">
                      {!activeAssignment.hasGroupWorkBoss ? (
                        <span
                          className="badge badge-secondary py-1 px-2"
                          title="ไม่มีหัวหน้ากลุ่มงาน (ข้ามระดับนี้อัตโนมัติ)"
                        >
                          2. กลุ่มงาน: -
                        </span>
                      ) : activeAssignment.groupWorkApprovedBy ? (
                        <span
                          className="badge badge-success py-1 px-2"
                          title={`อนุมัติโดย ${activeAssignment.groupWorkApprovedFullName ||
                            activeAssignment.groupWorkApprovedBy} เมื่อ ${formatThaiDate(
                            activeAssignment.groupWorkApprovedDate
                          )}`}
                        >
                          <i className="fas fa-check mr-1"></i>
                          2. หน.กลุ่มงาน:{" "}
                          {activeAssignment.groupWorkApprovedFullName ||
                            activeAssignment.groupWorkApprovedBy}
                        </span>
                      ) : activeAssignment.currentRequiredLevel === 2 ? (
                        <span
                          className="badge badge-warning py-1 px-2 font-weight-bold"
                          style={{
                            color: "#7c2d12",
                            backgroundColor: "#fef08a"
                          }}
                        >
                          <i className="fas fa-hourglass-half mr-1"></i>
                          2. รอ หน.กลุ่มงาน (
                          {activeAssignment.groupWorkBossFullName ||
                            "รออนุมัติ"}
                          )
                        </span>
                      ) : (
                        <span className="badge badge-light border text-muted py-1 px-2">
                          2. หน.กลุ่มงาน
                        </span>
                      )}
                    </div>

                    <i
                      className="fas fa-chevron-right text-muted mx-1"
                      style={{ fontSize: "10px" }}
                    ></i>

                    {/* Step 3: Mission */}
                    <div className="mr-1 mb-1">
                      {!activeAssignment.hasMissionBoss ? (
                        <span
                          className="badge badge-secondary py-1 px-2"
                          title="ไม่มีหัวหน้าภารกิจ (ข้ามระดับนี้อัตโนมัติ)"
                        >
                          3. ภารกิจ: -
                        </span>
                      ) : activeAssignment.missionApprovedBy ? (
                        <span
                          className="badge badge-success py-1 px-2"
                          title={`อนุมัติโดย ${activeAssignment.missionApprovedFullName ||
                            activeAssignment.missionApprovedBy} เมื่อ ${formatThaiDate(
                            activeAssignment.missionApprovedDate
                          )}`}
                        >
                          <i className="fas fa-check mr-1"></i>
                          3. หน.ภารกิจ:{" "}
                          {activeAssignment.missionApprovedFullName ||
                            activeAssignment.missionApprovedBy}
                        </span>
                      ) : activeAssignment.currentRequiredLevel === 3 ? (
                        <span
                          className="badge badge-warning py-1 px-2 font-weight-bold"
                          style={{
                            color: "#7c2d12",
                            backgroundColor: "#fef08a"
                          }}
                        >
                          <i className="fas fa-hourglass-end mr-1"></i>
                          3. รอ หน.ภารกิจ (
                          {activeAssignment.missionBossFullName || "รออนุมัติ"})
                        </span>
                      ) : (
                        <span className="badge badge-light border text-muted py-1 px-2">
                          3. หน.ภารกิจ
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* เกณฑ์ 5 ระดับ Cards (แยก 2 คอลัมน์ รอบ 6 เดือน และ 12 เดือน) */}
              <div className="mt-3 mb-2">
                <h6 className="font-weight-bold">
                  <i className="fas fa-list-ol mr-1"></i> เกณฑ์การให้คะแนน 5
                  ระดับ (Evaluation Criteria):
                </h6>
              </div>

              <div className="row">
                {/* คอลัมน์ซ้าย: รอบ 6 เดือน */}
                <div className="col-12 col-md-6 mb-3">
                  <div className="p-2 mb-2 rounded border border-primary bg-light d-flex justify-content-between align-items-center">
                    <span className="font-weight-bold text-primary">
                      <i className="far fa-calendar-alt mr-1"></i> เกณฑ์รอบ 6
                      เดือน
                    </span>
                    <span className="badge badge-primary">รอบที่ 1</span>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#dc3545",
                      backgroundColor: "#fff5f5"
                    }}
                  >
                    <strong className="text-danger">ระดับ 1:</strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {activeItem.criteriaLevel1 || "ไม่ได้ระบุ"}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#ffc107",
                      backgroundColor: "#fffdf0"
                    }}
                  >
                    <strong className="text-warning">ระดับ 2:</strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {activeItem.criteriaLevel2 || "ไม่ได้ระบุ"}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#007bff",
                      backgroundColor: "#f0f8ff"
                    }}
                  >
                    <strong className="text-primary">
                      ระดับ 3 (เป้าหมาย 6 เดือน):
                    </strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {activeItem.criteriaLevel3 || "ไม่ได้ระบุ"}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#17a2b8",
                      backgroundColor: "#f0fbff"
                    }}
                  >
                    <strong className="text-info">ระดับ 4:</strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {activeItem.criteriaLevel4 || "ไม่ได้ระบุ"}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#28a745",
                      backgroundColor: "#f0fff4"
                    }}
                  >
                    <strong className="text-success">
                      ระดับ 5 (ดีเยี่ยม):
                    </strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {activeItem.criteriaLevel5 || "ไม่ได้ระบุ"}
                    </p>
                  </div>
                </div>

                {/* คอลัมน์ขวา: รอบ 12 เดือน */}
                <div className="col-12 col-md-6 mb-3">
                  <div className="p-2 mb-2 rounded border border-success bg-light d-flex justify-content-between align-items-center">
                    <span className="font-weight-bold text-success">
                      <i className="far fa-calendar-check mr-1"></i> เกณฑ์รอบ 12
                      เดือน (สิ้นปี)
                    </span>
                    <span className="badge badge-success">รอบที่ 2</span>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#dc3545",
                      backgroundColor: "#fff5f5"
                    }}
                  >
                    <strong className="text-danger">ระดับ 1:</strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {activeItem.criteria12M_Level1 || (
                        <span className="text-muted font-italic">
                          {activeItem.criteriaLevel1 || "ไม่ได้ระบุ"}
                        </span>
                      )}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#ffc107",
                      backgroundColor: "#fffdf0"
                    }}
                  >
                    <strong className="text-warning">ระดับ 2:</strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {activeItem.criteria12M_Level2 || (
                        <span className="text-muted font-italic">
                          {activeItem.criteriaLevel2 || "ไม่ได้ระบุ"}
                        </span>
                      )}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#007bff",
                      backgroundColor: "#f0f8ff"
                    }}
                  >
                    <strong className="text-primary">
                      ระดับ 3 (เป้าหมาย 12 เดือน):
                    </strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {activeItem.criteria12M_Level3 || (
                        <span className="text-muted font-italic">
                          {activeItem.criteriaLevel3 || "ไม่ได้ระบุ"}
                        </span>
                      )}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#17a2b8",
                      backgroundColor: "#f0fbff"
                    }}
                  >
                    <strong className="text-info">ระดับ 4:</strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {activeItem.criteria12M_Level4 || (
                        <span className="text-muted font-italic">
                          {activeItem.criteriaLevel4 || "ไม่ได้ระบุ"}
                        </span>
                      )}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#28a745",
                      backgroundColor: "#f0fff4"
                    }}
                  >
                    <strong className="text-success">
                      ระดับ 5 (ดีเยี่ยม):
                    </strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {activeItem.criteria12M_Level5 || (
                        <span className="text-muted font-italic">
                          {activeItem.criteriaLevel5 || "ไม่ได้ระบุ"}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* สูตรคำนวณ */}
              <div
                className="card bg-light mt-3 p-3"
                style={{ fontSize: "13px" }}
              >
                <div className="mb-1">
                  <strong>
                    <i className="fas fa-calculator mr-1"></i> สูตรคำนวณ:
                  </strong>{" "}
                  {activeItem.calculationFormula || "-"}
                </div>
                <div>
                  <strong>
                    <i className="fas fa-database mr-1"></i> ข้อมูลพื้นฐาน
                    (Baseline):
                  </strong>{" "}
                  {activeItem.baselineData || "-"}
                </div>
              </div>

              <div className="text-right mt-3">
                <Button
                  color="secondary"
                  onClick={() => setIsDetailModalOpen(false)}
                >
                  ปิดหน้าต่าง
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ========================================================================= */}
      {/* 4. Modal บันทึกการประเมินตนเอง (Self-Assessment Modal) */}
      {/* ========================================================================= */}
      <Modal
        isOpen={isSelfAssessModalOpen}
        onRequestClose={() => setIsSelfAssessModalOpen(false)}
        style={{
          overlay: styles.modalOverlay,
          content: styles.modalContent
        }}
        contentLabel="บันทึกผลการประเมินตนเอง"
      >
        {activeItem && activeAssignment && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "2px solid #007bff",
                paddingBottom: "10px"
              }}
            >
              <h4 style={{ margin: 0, color: "#007bff", fontWeight: "bold" }}>
                <i className="fas fa-pencil-alt mr-2"></i>{" "}
                บันทึกผลการประเมินตนเอง (Self Assessment)
              </h4>
              <button
                type="button"
                className="close"
                onClick={() => setIsSelfAssessModalOpen(false)}
              >
                <span>&times;</span>
              </button>
            </div>

            <div className="mt-3">
              <div
                className="alert alert-info py-2"
                style={{ fontSize: "13px" }}
              >
                <strong>ตัวชี้วัด:</strong> {activeItem.kpiName} |{" "}
                <strong>น้ำหนัก:</strong> {activeAssignment.weight}% |{" "}
                <strong>เป้าหมาย:</strong> ระดับ {activeItem.targetScoreLevel}
              </div>

              {/* อ้างอิงเกณฑ์ประเมิน */}
              <div
                className="card bg-light p-3 mb-3"
                style={{
                  fontSize: "12px",
                  maxHeight: "150px",
                  overflowY: "auto"
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <strong>เกณฑ์ 5 ระดับเพื่อการอ้างอิง:</strong>
                  <span
                    className={`badge ${
                      currentPeriod?.kpiRound === 2
                        ? "badge-success"
                        : "badge-primary"
                    }`}
                  >
                    {currentPeriod?.kpiRound === 2
                      ? "เกณฑ์รอบ 12 เดือน"
                      : "เกณฑ์รอบ 6 เดือน"}
                  </span>
                </div>
                <div>
                  ระดับ 1:{" "}
                  {currentPeriod?.kpiRound === 2 &&
                  activeItem.criteria12M_Level1
                    ? activeItem.criteria12M_Level1
                    : activeItem.criteriaLevel1 || "-"}
                </div>
                <div>
                  ระดับ 2:{" "}
                  {currentPeriod?.kpiRound === 2 &&
                  activeItem.criteria12M_Level2
                    ? activeItem.criteria12M_Level2
                    : activeItem.criteriaLevel2 || "-"}
                </div>
                <div className="text-primary font-weight-bold">
                  ระดับ 3 (เป้าหมาย):{" "}
                  {currentPeriod?.kpiRound === 2 &&
                  activeItem.criteria12M_Level3
                    ? activeItem.criteria12M_Level3
                    : activeItem.criteriaLevel3 || "-"}
                </div>
                <div>
                  ระดับ 4:{" "}
                  {currentPeriod?.kpiRound === 2 &&
                  activeItem.criteria12M_Level4
                    ? activeItem.criteria12M_Level4
                    : activeItem.criteriaLevel4 || "-"}
                </div>
                <div className="text-success">
                  ระดับ 5:{" "}
                  {currentPeriod?.kpiRound === 2 &&
                  activeItem.criteria12M_Level5
                    ? activeItem.criteria12M_Level5
                    : activeItem.criteriaLevel5 || "-"}
                </div>
              </div>

              <form onSubmit={handleSubmitSelfAssessment}>
                <div className="form-group">
                  <label className="font-weight-bold">
                    ผลการดำเนินงานที่เกิดขึ้นจริง (Actual Achievement){" "}
                    <span className="text-danger">*</span>
                  </label>
                  <textarea
                    rows={3}
                    className="form-control"
                    value={selfAssessFormData.ActualAchievement}
                    onChange={e =>
                      setSelfAssessFormData({
                        ...selfAssessFormData,
                        ActualAchievement: e.target.value
                      })
                    }
                    placeholder="ระบุผลสำเร็จของงานที่ได้ทำจริง ตัวเลข ผลลัพธ์ หรือร้อยละความสำเร็จ..."
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label className="font-weight-bold text-primary">
                      ระดับคะแนนที่ประเมินตนเอง (1 - 5){" "}
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control font-weight-bold text-primary"
                      value={selfAssessFormData.SelfScoreLevel}
                      onChange={e =>
                        setSelfAssessFormData({
                          ...selfAssessFormData,
                          SelfScoreLevel: parseInt(e.target.value, 10)
                        })
                      }
                      required
                    >
                      <option value={1}>ระดับ 1</option>
                      <option value={2}>ระดับ 2</option>
                      <option value={3}>ระดับ 3</option>
                      <option value={4}>ระดับ 4</option>
                      <option value={5}>ระดับ 5</option>
                    </select>
                  </div>

                  <div className="form-group col-md-6">
                    <label className="font-weight-bold">
                      คะแนนที่ได้คำนวณตามแบบ ปผ.
                    </label>
                    <div className="form-control bg-light text-success font-weight-bold">
                      {selfAssessFormData.SelfScoreLevel *
                        activeAssignment.weight}{" "}
                      คะแนน (เต็ม {5 * activeAssignment.weight} คะแนน)
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="font-weight-bold">
                    แนบลิงก์หลักฐาน / เอกสารอ้างอิง (Attachment File URLs)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={selfAssessFormData.AttachmentFileUrls}
                    onChange={e =>
                      setSelfAssessFormData({
                        ...selfAssessFormData,
                        AttachmentFileUrls: e.target.value
                      })
                    }
                    placeholder="วางลิงก์เอกสาร Google Drive / OneDrive หรือไฟล์หลักฐาน"
                  />
                </div>

                <div className="text-right border-top pt-3 mt-3">
                  <Button
                    color="transparent"
                    onClick={() => setIsSelfAssessModalOpen(false)}
                    disabled={isSaving}
                  >
                    ยกเลิก
                  </Button>
                  <Button color="primary" type="submit" disabled={isSaving}>
                    {isSaving ? (
                      <i className="fas fa-spinner fa-spin mr-1"></i>
                    ) : (
                      <i className="fas fa-check mr-1"></i>
                    )}
                    ส่งผลการประเมินตนเอง
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal 5: คำชี้แจงส่งกลับแก้ไขและการตอบกลับ */}
      <Modal
        isOpen={isClarifyModalOpen}
        onRequestClose={handleCloseClarifyModal}
        style={{
          overlay: styles.modalOverlay,
          content: {
            ...styles.modalContent,
            width: "700px",
            maxHeight: "85vh"
          }
        }}
        contentLabel="คำชี้แจงส่งกลับแก้ไขและการตอบกลับ"
      >
        {clarifyingAssignment && (
          <div>
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "2px solid #ef4444",
                paddingBottom: "12px",
                marginBottom: "20px"
              }}
            >
              <div>
                <h4
                  style={{
                    margin: 0,
                    color: "#dc2626",
                    fontWeight: "bold"
                  }}
                >
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  คำชี้แจงการส่งกลับแก้ไขตัวชี้วัด
                </h4>
                <div className="text-muted small mt-1">
                  {clarifyingAssignment.kpiItem?.code
                    ? `[${clarifyingAssignment.kpiItem.code}] `
                    : ""}
                  {clarifyingAssignment.kpiItem?.kpiName} (สัดส่วน:{" "}
                  {clarifyingAssignment.weight}%)
                </div>
              </div>
              <button
                type="button"
                className="close"
                style={{ fontSize: "28px" }}
                onClick={handleCloseClarifyModal}
              >
                <span>&times;</span>
              </button>
            </div>

            {/* Body */}
            <div>
              {/* ข้อเสนอแนะ/คำชี้แจงจากหัวหน้า */}
              <div
                className="p-3 mb-3 rounded"
                style={{
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca"
                }}
              >
                <div
                  className="font-weight-bold mb-2 text-danger"
                  style={{ fontSize: "14px" }}
                >
                  <i className="fas fa-comment-dots mr-1"></i> คำชี้แจง /
                  เหตุผลที่ส่งกลับแก้ไขจากผู้บังคับบัญชา:
                </div>
                <div
                  className="text-dark pl-3"
                  style={{
                    borderLeft: "3px solid #ef4444",
                    fontSize: "14px",
                    whiteSpace: "pre-wrap"
                  }}
                >
                  {clarifyingAssignment.assignmentRemark ||
                    "ไม่มีคำชี้แจงเพิ่มเติม (กรุณาตรวจสอบรายละเอียดตัวชี้วัด)"}
                </div>
              </div>

              {/* ฟอร์มตอบกลับคำชี้แจง */}
              <div className="form-group mb-3">
                <label
                  className="font-weight-bold text-dark mb-1"
                  style={{ fontSize: "14px" }}
                >
                  <i className="fas fa-reply text-primary mr-1"></i>{" "}
                  ตอบกลับคำชี้แจง / รายละเอียดการปรับปรุงแก้ไข:{" "}
                  <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  rows="4"
                  style={{ fontSize: "14px", lineHeight: "1.5" }}
                  placeholder="ระบุคำชี้แจง ข้อมูลเพิ่มเติม หรือการปรับปรุงตัวชี้วัดเพื่อเสนอให้หัวหน้างานพิจารณาอนุมัติอีกครั้ง..."
                  value={clarificationText}
                  onChange={e => setClarificationText(e.target.value)}
                ></textarea>
                <small className="form-text text-muted mt-1">
                  <i className="fas fa-info-circle text-info mr-1"></i>
                  เมื่อท่านกด &quot;ส่งคำชี้แจงและขออนุมัติใหม่&quot;
                  ระบบจะเปลี่ยนสถานะตัวชี้วัดกลับมาเป็น &quot;รออนุมัติ&quot;
                  และหัวหน้างานจะเห็นข้อความตอบกลับนี้ทันที
                </small>
              </div>

              {/* Actions Footer */}
              <div className="text-right border-top pt-3 mt-3">
                <Button
                  color="transparent"
                  onClick={handleCloseClarifyModal}
                  disabled={isSubmittingClarification}
                >
                  ยกเลิก
                </Button>
                <button
                  type="button"
                  className="btn btn-primary ml-2 px-3 py-2 font-weight-bold"
                  disabled={
                    isSubmittingClarification || !clarificationText.trim()
                  }
                  onClick={handleSubmitClarification}
                >
                  {isSubmittingClarification ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-1"></i>{" "}
                      กำลังส่ง...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane mr-1"></i>{" "}
                      ส่งคำชี้แจงและขออนุมัติใหม่
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ========================================================================= */}
      {/* MODAL: ปรับปรุงน้ำหนักตัวชี้วัดส่วนตัว (Edit Personal Weight)              */}
      {/* ========================================================================= */}
      <Modal
        isOpen={isWeightModalOpen}
        onRequestClose={() => {
          if (!isSavingWeight) {
            setIsWeightModalOpen(false);
            setWeightAssignment(null);
          }
        }}
        style={{
          overlay: styles.modalOverlay,
          content: {
            ...styles.modalContent,
            maxWidth: "520px"
          }
        }}
        contentLabel="ปรับปรุงน้ำหนักตัวชี้วัดส่วนตัว"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "2px solid #007bff",
            paddingBottom: "10px"
          }}
        >
          <h4 style={{ margin: 0, color: "#007bff", fontWeight: "bold" }}>
            <i className="fas fa-weight-hanging mr-2"></i>
            ปรับปรุงน้ำหนักตัวชี้วัดส่วนตัว
          </h4>
          <button
            type="button"
            className="close"
            disabled={isSavingWeight}
            onClick={() => {
              setIsWeightModalOpen(false);
              setWeightAssignment(null);
            }}
          >
            <span>&times;</span>
          </button>
        </div>

        {weightAssignment && (
          <form onSubmit={handleSaveWeight} className="mt-3">
            <div className="alert alert-light border">
              <div
                className="font-weight-bold text-dark"
                style={{ fontSize: "15px" }}
              >
                {weightAssignment.kpiItem?.code
                  ? `[${weightAssignment.kpiItem.code}] `
                  : ""}
                {weightAssignment.kpiItem?.kpiName}
              </div>
              <div className="text-muted mt-1 small">
                ประเภท:{" "}
                {weightAssignment.kpiItem?.kpiType === 1
                  ? "ตัวชี้วัดร่วม/ภารกิจ (Common KPI)"
                  : "ตัวชี้วัดส่วนบุคคล (Individual KPI)"}
              </div>
            </div>

            <div className="form-group">
              <label className="font-weight-bold">
                น้ำหนักตัวชี้วัดส่วนตัว (%) <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  required
                  className="form-control form-control-lg font-weight-bold text-primary"
                  value={weightInputValue}
                  onChange={e => setWeightInputValue(e.target.value)}
                  placeholder="0 - 100"
                  autoFocus
                />
                <div className="input-group-append">
                  <span className="input-group-text font-weight-bold">%</span>
                </div>
              </div>
              <small className="form-text text-muted">
                กำหนดค่าน้ำหนักของตัวชี้วัดนี้สำหรับตนเอง โดยผลรวมของทุกตัวชี้วัดในรอบต้องได้ 100%
              </small>
            </div>

            {/* คำนวณผลรวมน้ำหนักจำลอง */}
            {(() => {
              const oldW = Number(weightAssignment.weight) || 0;
              const newW = parseFloat(weightInputValue) || 0;
              const projectedTotal = displayedTotalWeight - oldW + newW;
              const isProjectedValid = Math.abs(projectedTotal - 100) < 0.01;
              return (
                <div
                  className={`alert ${
                    isProjectedValid ? "alert-success" : "alert-warning"
                  } py-2 mb-3`}
                >
                  <div className="d-flex justify-content-between align-items-center small">
                    <span>
                      น้ำหนักเดิม: <b>{oldW}%</b>
                    </span>
                    <span>
                      น้ำหนักรวมหลังปรับ:{" "}
                      <b
                        className={
                          isProjectedValid ? "text-success" : "text-danger"
                        }
                      >
                        {projectedTotal.toFixed(1)}%
                      </b>
                    </span>
                  </div>
                </div>
              );
            })()}

            <div className="d-flex justify-content-end mt-4 pt-2 border-top">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                disabled={isSavingWeight}
                onClick={() => {
                  setIsWeightModalOpen(false);
                  setWeightAssignment(null);
                }}
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                className="btn btn-primary font-weight-bold"
                disabled={isSavingWeight || weightInputValue === ""}
              >
                {isSavingWeight ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-1"></i>{" "}
                    กำลังบันทึก...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-1"></i> บันทึกน้ำหนัก
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default MyKpi;
